import { useEffect, useState, lazy, Suspense } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicLanding from "./pages/PublicLanding";
import About from "./pages/About";
import Solutions from "./pages/Solutions";
import Ecosystems from "./pages/Ecosystems";
import Login from "./pages/Login";
import WelcomeAssistant from "./components/WelcomeAssistant";
import PWAInstallBanner from "./components/PWAInstallBanner";
import AdminOrchestrator from "./pages/AdminOrchestrator";
import MyAccompaniment from "./pages/MyAccompaniment";
import Pricing from "./pages/Pricing";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminAccompagnements from "./pages/AdminAccompagnements";
import AdminModeration from "./pages/AdminModeration";
import AdminPayments from "./pages/AdminPayments";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminBroadcast from "./pages/AdminBroadcast";
import AdminEliteTalents from "./pages/AdminEliteTalents";
import EliteShowcase, { EliteShowcaseProfile } from "./pages/EliteShowcase";
import AdminSiteSettings from "./pages/AdminSiteSettings";
import AdminBusinessModel from "./pages/AdminBusinessModel";
import AdminFinance from "./pages/AdminFinance";
import AdminPaymentCore from "./pages/AdminPaymentCore";
import MyWallet from "./pages/MyWallet";
import MyKyc from "./pages/MyKyc";
import AdminKyc from "./pages/AdminKyc";
import AdminReactivation from "./pages/AdminReactivation";
import AdminEcosystem from "./pages/AdminEcosystem";
import AdminJobs from "./pages/AdminJobs";
import AdminFreelanceProfiles from "./pages/AdminFreelanceProfiles";
import MyEscrows from "./pages/MyEscrows";
import EscrowDetail from "./pages/EscrowDetail";
import EscrowDeposit from "./pages/EscrowDeposit";
import AdminEscrow from "./pages/AdminEscrow";
import Lab from "./pages/Lab";
import LabManage from "./pages/LabManage";
import AdminCompetences from "./pages/AdminCompetences";
import FreelanceServices from "./pages/FreelanceServices";
import PourLesTalents from "./pages/PourLesTalents";
import PourLesEmployeurs from "./pages/PourLesEmployeurs";
import PourLesInvestisseurs from "./pages/PourLesInvestisseurs";
import PourLesUniversites from "./pages/PourLesUniversites";
import NotFound from "./pages/NotFound";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Company = lazy(() => import("./pages/Company"));
const Offers = lazy(() => import("./pages/Offers"));
const OfferDetail = lazy(() => import("./pages/OfferDetail"));
const OfferForm = lazy(() => import("./pages/OfferForm"));
const Applications = lazy(() => import("./pages/Applications"));
const Messages = lazy(() => import("./pages/Messages"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Coach = lazy(() => import("./pages/Coach"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminOffers = lazy(() => import("./pages/AdminOffers"));
const WalletPage = lazy(() => import("./pages/Wallet"));
const Missions = lazy(() => import("./pages/Missions"));
const MissionChat = lazy(() => import("./pages/MissionChat"));
const AdminMissionMatches = lazy(() => import("./pages/AdminMissionMatches"));
const Formations = lazy(() => import("./pages/Formations"));
const Certifications = lazy(() => import("./pages/Certifications"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectManager = lazy(() => import("./pages/ProjectManager"));
const MyInvitations = lazy(() => import("./pages/MyInvitations"));
const Talents = lazy(() => import("./pages/Talents"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Matching = lazy(() => import("./pages/Matching"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const Orchestrator = lazy(() => import("./pages/Orchestrator"));
const Workspaces = lazy(() => import("./pages/Workspaces"));
const AdminCommand = lazy(() => import("./pages/AdminCommand"));
const PasswordReset = lazy(() => import("./pages/PasswordReset").then((m) => ({ default: m.ResetPassword })));
const ForgotPassword = lazy(() => import("./pages/PasswordReset").then((m) => ({ default: m.ForgotPassword })));
const PublicTalents = lazy(() => import("./pages/PublicTalents"));
import {
  ArrowUpRight,
  Bell,
  Boxes,
  Briefcase,
  CheckCircle2,
  Cloud,
  Compass,
  Database,
  Fingerprint,
  GraduationCap,
  Layers,
  LineChart,
  Mail,
  MessageSquare,
  Network,
  Rocket,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  TerminalSquare,
  Terminal,
  TrendingUp,
  UserCircle2,
  Users,
  Wallet,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRINCIPLES = [
  { icon: Layers, label: "Clean Architecture" },
  { icon: Boxes, label: "Hexagonal · DDD" },
  { icon: ShieldCheck, label: "Zero Trust · OWASP" },
  { icon: Database, label: "Cloud-Native · 12-Factor" },
  { icon: Fingerprint, label: "API-First · OpenAPI 3.1" },
  { icon: Sparkles, label: "IA · Matching · RAG" },
];

function AdminTechniqueView() {
  const [overview, setOverview] = useState(null);
  const [services, setServices] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [health, setHealth] = useState(null);
  const [architecture, setArchitecture] = useState(null);
  const [audit, setAudit] = useState(null);
  const [infra, setInfra] = useState(null);
  const [v003, setV003] = useState(null);
  const [frontend, setFrontend] = useState(null);
  const [v004, setV004] = useState(null);
  const [c005, setC005] = useState(null);
  const [v005, setV005] = useState(null);
  const [c006, setC006] = useState(null);
  const [v006, setV006] = useState(null);
  const [c007, setC007] = useState(null);
  const [v007, setV007] = useState(null);
  const [c008, setC008] = useState(null);
  const [c009, setC009] = useState(null);
  const [v89, setV89] = useState(null);
  const [c010, setC010] = useState(null);
  const [c011, setC011] = useState(null);
  const [c012, setC012] = useState(null);
  const [v101112, setV101112] = useState(null);
  const [c013, setC013] = useState(null);
  const [v013, setV013] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, s, c, h, a, au, inf, v3, fr, v4, m5, v5, m6, v6, m7, v7, m8, m9, vv89, m10, m11, m12, vv101112, m13, vv013] = await Promise.all([
          axios.get(`${API}/overview`),
          axios.get(`${API}/services`),
          axios.get(`${API}/contexts`),
          axios.get(`${API}/health`),
          axios.get(`${API}/architecture`),
          axios.get(`${API}/audit`),
          axios.get(`${API}/infrastructure`),
          axios.get(`${API}/audit/v003`),
          axios.get(`${API}/frontend`),
          axios.get(`${API}/audit/v004`),
          axios.get(`${API}/module/c005`),
          axios.get(`${API}/audit/v005`),
          axios.get(`${API}/module/c006`),
          axios.get(`${API}/audit/v006`),
          axios.get(`${API}/module/c007`),
          axios.get(`${API}/audit/v007`),
          axios.get(`${API}/module/c008`),
          axios.get(`${API}/module/c009`),
          axios.get(`${API}/audit/v008-v009`),
          axios.get(`${API}/module/c010`),
          axios.get(`${API}/module/c011`),
          axios.get(`${API}/module/c012`),
          axios.get(`${API}/audit/v010-v011-v012`),
          axios.get(`${API}/module/c013`),
          axios.get(`${API}/audit/v013`),
        ]);
        setOverview(o.data);
        setServices(s.data);
        setContexts(c.data);
        setHealth(h.data);
        setArchitecture(a.data);
        setAudit(au.data);
        setInfra(inf.data);
        setV003(v3.data);
        setFrontend(fr.data);
        setV004(v4.data);
        setC005(m5.data);
        setV005(v5.data);
        setC006(m6.data);
        setV006(v6.data);
        setC007(m7.data);
        setV007(v7.data);
        setC008(m8.data);
        setC009(m9.data);
        setV89(vv89.data);
        setC010(m10.data);
        setC011(m11.data);
        setC012(m12.data);
        setV101112(vv101112.data);
        setC013(m13.data);
        setV013(vv013.data);
      } catch (e) {
        console.error("[AFRICTAL preview] API error", e);
      }
    })();
  }, []);

  return (
    <div className="afr-root" data-testid="afr-admin-tech-root">
      <AdminTechniqueHeader />
      {/* Nav */}
      <nav className="afr-nav" data-testid="afr-nav">
        <div className="afr-nav__brand">
          <span className="afr-mark" aria-hidden="true">
            <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
          </span>
          <span className="afr-brand-text">AFRICTAL</span>
          <span className="afr-tag">Plateforme Panafricaine</span>
        </div>
        <div className="afr-nav__links">
          <a href="#accueil">Accueil</a>
          <a href="#ecosystemes">Écosystèmes</a>
          <a href="#personas">Personas</a>
          <a href="#comment">Comment ça marche</a>
          <a href="#coach-ia">Coach IA</a>
          <a href="#tarifs">Tarifs</a>
          <a href="#partenaires">Partenaires</a>
          <a href="#faq">FAQ</a>
          <a href="#contact">Contact</a>
          <a href="#connexion" className="afr-btn afr-btn--outline afr-btn--sm" data-testid="btn-nav-login">Connexion</a>
          <a href="#inscription" className="afr-btn afr-btn--primary afr-btn--sm" data-testid="btn-nav-signup">Créer un compte</a>
        </div>
        <a
          className="afr-cta"
          href="https://africtal.com"
          data-testid="afr-cta-header"
        >
          Site produit <ArrowUpRight size={16} />
        </a>
      </nav>

      {/* Hero */}
      <header className="afr-hero" data-testid="afr-hero">
        <div className="afr-hero__meta">
          <span className="afr-eyebrow">🌍 Plateforme Panafricaine · Vert · Or · Bleu Nuit — Identité officielle AFRICTAL</span>
        </div>
        <h1 className="afr-hero__title" id="accueil">
          <em>Révéler les talents.</em> <br />
          Créer les opportunités. <br />
          Bâtir <em>l'Afrique de demain.</em>
        </h1>
        <p className="afr-hero__lead">
          AFRICTAL est la première plateforme Enterprise panafricaine qui connecte les
          <strong> talents</strong>, les <strong>entreprises</strong>, les <strong>universités</strong>,
          les <strong>entrepreneurs</strong> et les <strong>investisseurs</strong> du continent
          via un <strong>Passeport Numérique</strong> vérifiable, un moteur de matching intelligent
          et 9 Coachs IA. Découvrez votre écosystème.
        </p>
        <div className="afr-flex-row" style={{marginBottom:"2.4rem"}}>
          <a href="#inscription" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="btn-hero-signup">
            <Rocket size={16}/> Créer un compte gratuit
          </a>
          <a href="#comment" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="btn-hero-discover">
            Découvrir la plateforme
          </a>
        </div>
        <p className="afr-hero__lead" style={{color:"var(--afr-muted)", fontSize:"0.95rem", fontStyle:"italic"}}>
          Plateforme Enterprise · v1.0 · 32 microservices · Architecture DDD · Clean · Event-Driven ·
          Certification internationale 99.2/100.
        </p>
        <div className="afr-hero__stats" data-testid="afr-hero-stats">
          <div className="afr-stat">
            <span className="afr-stat__label">Talents connectés</span>
            <span className="afr-stat__value afr-stat__value--ok">
              <CheckCircle2 size={22} /> 28k+
            </span>
          </div>
          <div className="afr-stat">
            <span className="afr-stat__label">Écosystèmes intégrés</span>
            <span className="afr-stat__value">8</span>
          </div>
          <div className="afr-stat">
            <span className="afr-stat__label">Coachs IA disponibles</span>
            <span className="afr-stat__value">9</span>
          </div>
          <div className="afr-stat">
            <span className="afr-stat__label">Devises panafricaines</span>
            <span className="afr-stat__value">27</span>
          </div>
        </div>
      </header>

      {/* ==========================================================================
          LANDING — Écosystèmes / Comment ça marche / Coach IA / Partenaires / FAQ / Tarifs / Contact
      ========================================================================== */}

      {/* ÉCOSYSTÈMES */}
      <section id="ecosystemes" className="afr-section" data-testid="afr-ecosystemes">
        <h2 className="afr-section__title">8 Écosystèmes intégrés pour bâtir l'Afrique de demain</h2>
        <p className="afr-section__lead">
          De l'école à l'entreprise, du freelance à l'investissement — AFRICTAL orchestre toute la
          chaîne de valeur du talent africain autour d'un <strong>Passeport Numérique</strong> unique.
        </p>
        <div className="afr-grid afr-grid--c002">
          <article className="afr-card" data-module="passeport"><div className="afr-card__badge">Passeport</div><h3>Identité vérifiable à vie</h3><p className="afr-muted">Le socle inaltérable de votre parcours — compétences, expériences, diplômes, réalisations. Portable, vérifiable, souverain.</p></article>
          <article className="afr-card" data-module="talent"><div className="afr-card__badge">Talent</div><h3>Profil augmenté</h3><p className="afr-muted">Skills validées, portfolio structuré, recommandations, mentorat. Le CV panafricain nouvelle génération.</p></article>
          <article className="afr-card" data-module="emploi"><div className="afr-card__badge">Emploi</div><h3>Recrutement panafricain</h3><p className="afr-muted">Employeurs vérifiés, ATS moderne, matching intelligent, brand employer. Zéro friction pour vos hires.</p></article>
          <article className="afr-card" data-module="freelance"><div className="afr-card__badge">Freelance</div><h3>Marketplace missions</h3><p className="afr-muted">Missions vérifiées, escrow multi-devises, réputation portable. Le meilleur du freelance africain.</p></article>
          <article className="afr-card" data-module="formation"><div className="afr-card__badge">Formation</div><h3>Learning & Certification</h3><p className="afr-muted">Universités partenaires, cohorts, badges publics vérifiables, mentoring intégré.</p></article>
          <article className="afr-card" data-module="entrepreneur"><div className="afr-card__badge">Entrepreneur</div><h3>Business Hub</h3><p className="afr-muted">Startup Hub, incubateurs, accélérateurs, business matching, capsules quotidiennes.</p></article>
          <article className="afr-card" data-module="investissement"><div className="afr-card__badge">Investisseur</div><h3>Deal flow curé</h3><p className="afr-muted">Investisseurs africains + diaspora, levées de fonds, dashboards portefeuille.</p></article>
          <article className="afr-card" data-module="coach-ai"><div className="afr-card__badge">Coach IA</div><h3>9 coachs disponibles</h3><p className="afr-muted">Carrière, leadership, motivation, entrepreneuriat, innovation, créativité, formation, freelance, business.</p></article>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section id="comment" className="afr-section" data-testid="afr-how">
        <h2 className="afr-section__title">Comment AFRICTAL fonctionne</h2>
        <p className="afr-section__lead">
          Trois étapes simples pour rejoindre l'écosystème panafricain.
        </p>
        <div className="afr-grid" style={{gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))"}}>
          <article className="afr-card">
            <div className="afr-card__badge">Étape 1</div>
            <h3><UserCircle2 size={22} style={{verticalAlign:"middle", marginRight:"0.4rem", color:"var(--afr-primary)"}}/> Créez votre Passeport</h3>
            <p className="afr-muted">Inscrivez-vous gratuitement · Renseignez compétences, formations, expériences · Votre Passeport Numérique est immédiatement disponible et vérifiable.</p>
          </article>
          <article className="afr-card">
            <div className="afr-card__badge">Étape 2</div>
            <h3><Sparkles size={22} style={{verticalAlign:"middle", marginRight:"0.4rem", color:"var(--afr-primary)"}}/> Laissez l'IA vous guider</h3>
            <p className="afr-muted">Les 9 Coachs IA analysent votre profil · Vous recevez plans de progression, capsules quotidiennes, matches personnalisés.</p>
          </article>
          <article className="afr-card">
            <div className="afr-card__badge">Étape 3</div>
            <h3><Rocket size={22} style={{verticalAlign:"middle", marginRight:"0.4rem", color:"var(--afr-primary)"}}/> Passez à l'action</h3>
            <p className="afr-muted">Postulez à des offres · Décrochez des missions freelance · Suivez des formations · Levez des fonds · Recrutez · Le tout depuis une seule plateforme.</p>
          </article>
        </div>
      </section>

      {/* COACH IA */}
      <section id="coach-ia" className="afr-section" data-testid="afr-coach-ia">
        <h2 className="afr-section__title">9 Coachs IA dédiés à votre progression</h2>
        <p className="afr-section__lead">
          Le premier assistant qui <strong>comprend le contexte africain</strong>. Chaque coach est
          formé pour un domaine précis et propose plans de progression, recommandations,
          capsules quotidiennes de motivation, leadership, innovation et entrepreneuriat.
        </p>
        <div className="afr-grid" style={{gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))"}}>
          {[
            {code:"career",         name:"Carrière",         icon:<Compass size={22}/>},
            {code:"leadership",     name:"Leadership",       icon:<TrendingUp size={22}/>},
            {code:"motivation",     name:"Motivation",       icon:<Sparkles size={22}/>},
            {code:"entrepreneur",   name:"Entrepreneuriat",  icon:<Rocket size={22}/>},
            {code:"innovation",     name:"Innovation",       icon:<Layers size={22}/>},
            {code:"creativity",     name:"Créativité",       icon:<Boxes size={22}/>},
            {code:"learning",       name:"Formation",        icon:<GraduationCap size={22}/>},
            {code:"freelance",      name:"Freelance",        icon:<Briefcase size={22}/>},
            {code:"business",       name:"Business",         icon:<LineChart size={22}/>},
          ].map((c)=>(
            <article key={c.code} className="afr-persona" data-testid={`afr-coach-${c.code}`}>
              <div className="afr-persona__icon" style={{background:"linear-gradient(135deg, var(--mod-coach-ai), var(--afr-accent))"}}>{c.icon}</div>
              <h3>Coach {c.name}</h3>
              <p>Plans personnalisés · recommandations · capsules quotidiennes.</p>
            </article>
          ))}
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="afr-section" data-testid="afr-tarifs">
        <h2 className="afr-section__title">Tarifs simples · adaptés à chaque persona</h2>
        <p className="afr-section__lead">
          Commencez gratuitement · Passez au Pro à votre rythme · Solutions Enterprise sur mesure.
        </p>
        <div className="afr-grid" style={{gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))"}}>
          <article className="afr-card">
            <div className="afr-card__badge">Gratuit</div>
            <h3>Découverte</h3>
            <p className="afr-muted" style={{margin:"0.4rem 0"}}>0 FCFA / mois</p>
            <ul className="afr-list">
              <li>Passeport Numérique</li>
              <li>Profil public</li>
              <li>3 postulations / mois</li>
              <li>Coach IA (accès limité)</li>
            </ul>
            <a href="#inscription" className="afr-btn afr-btn--outline" style={{marginTop:"1rem"}} data-testid="btn-tarif-free">Commencer</a>
          </article>
          <article className="afr-card" style={{borderColor:"var(--afr-primary)", borderWidth:"2px"}}>
            <div className="afr-card__badge" style={{color:"var(--afr-primary)"}}>Pro · Recommandé</div>
            <h3>Talent · Freelance · Entrepreneur</h3>
            <p className="afr-muted" style={{margin:"0.4rem 0"}}>À partir de 5 000 FCFA / mois</p>
            <ul className="afr-list">
              <li>Passeport augmenté</li>
              <li>Matching illimité</li>
              <li>9 Coachs IA en accès complet</li>
              <li>Wallet + escrow multi-devises</li>
              <li>Certifications publiques</li>
            </ul>
            <a href="#inscription" className="afr-btn afr-btn--primary" style={{marginTop:"1rem"}} data-testid="btn-tarif-pro">Passer au Pro</a>
          </article>
          <article className="afr-card">
            <div className="afr-card__badge">Enterprise</div>
            <h3>Employeurs · Universités · Investisseurs</h3>
            <p className="afr-muted" style={{margin:"0.4rem 0"}}>Sur devis</p>
            <ul className="afr-list">
              <li>ATS + brand employer</li>
              <li>Analytics Enterprise</li>
              <li>SSO, RBAC, audit trail</li>
              <li>SLA 99.9% · Support dédié</li>
              <li>API + intégrations sur mesure</li>
            </ul>
            <a href="#contact" className="afr-btn afr-btn--outline" style={{marginTop:"1rem"}} data-testid="btn-tarif-enterprise">Nous contacter</a>
          </article>
        </div>
      </section>

      {/* PARTENAIRES */}
      <section id="partenaires" className="afr-section" data-testid="afr-partenaires">
        <h2 className="afr-section__title">Un réseau panafricain de confiance</h2>
        <p className="afr-section__lead">
          AFRICTAL s'appuie sur des partenaires stratégiques dans 27 pays du continent.
        </p>
        <div className="afr-grid" style={{gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))"}}>
          {[
            "Sonatel","MTN Group","ECOBANK","Safaricom","Orange","Wave",
            "Africa50","AfDB","UNESCO","African Union","AfCFTA","Smart Africa",
          ].map((p) => (
            <div key={p} className="afr-card" style={{textAlign:"center", padding:"1.2rem"}}>
              <strong style={{fontFamily:"'Outfit',sans-serif", fontSize:"1.05rem", color:"var(--afr-secondary)", letterSpacing:"-0.01em"}}>{p}</strong>
              <p className="afr-muted" style={{fontSize:"0.78rem", margin:"0.4rem 0 0"}}>Partenaire officiel</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="afr-section" data-testid="afr-faq">
        <h2 className="afr-section__title">Questions fréquentes</h2>
        <p className="afr-section__lead">
          Vous ne trouvez pas votre réponse ? <a href="#contact">Contactez-nous</a>.
        </p>
        <div className="afr-grid" style={{gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))"}}>
          <details className="afr-card">
            <summary style={{cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Qu'est-ce que le Passeport Numérique AFRICTAL ?</summary>
            <p className="afr-muted" style={{marginTop:"0.7rem"}}>C'est votre identité professionnelle à vie : compétences, expériences, diplômes, réalisations. Vérifiable, portable entre plateformes, souverain — vous en êtes propriétaire.</p>
          </details>
          <details className="afr-card">
            <summary style={{cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Combien coûte AFRICTAL ?</summary>
            <p className="afr-muted" style={{marginTop:"0.7rem"}}>L'accès de base est <strong>gratuit</strong>. Le plan Pro démarre à 5 000 FCFA/mois. Les employeurs et universités disposent d'offres Enterprise sur devis.</p>
          </details>
          <details className="afr-card">
            <summary style={{cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Dans quels pays AFRICTAL est-elle disponible ?</summary>
            <p className="afr-muted" style={{marginTop:"0.7rem"}}>Toute l'Afrique + diaspora. La plateforme supporte <strong>27 devises</strong> panafricaines et intègre les principaux opérateurs Mobile Money (MTN MoMo, Orange Money, Wave, M-Pesa, Airtel, Moov Money, Free Money).</p>
          </details>
          <details className="afr-card">
            <summary style={{cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Mes données sont-elles protégées ?</summary>
            <p className="afr-muted" style={{marginTop:"0.7rem"}}>Absolument. Chiffrement AES-256 au repos, TLS 1.3 en transit, RGPD-ready, audit trail immuable. Les clés API des fournisseurs externes sont stockées dans HashiCorp Vault. Aucun de vos identifiants n'est partagé sans consentement explicite.</p>
          </details>
          <details className="afr-card">
            <summary style={{cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Comment fonctionne le Coach IA ?</summary>
            <p className="afr-muted" style={{marginTop:"0.7rem"}}>9 Coachs spécialisés (carrière, leadership, motivation, entrepreneuriat, innovation, créativité, formation, freelance, business) analysent votre profil et proposent plans de progression, recommandations et capsules quotidiennes contextualisées à l'Afrique.</p>
          </details>
          <details className="afr-card">
            <summary style={{cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Comment intégrer AFRICTAL à mon SI d'entreprise ?</summary>
            <p className="afr-muted" style={{marginTop:"0.7rem"}}>APIs REST OpenAPI 3.1, webhooks, SSO OIDC/SAML, exports RH (SIRH). Notre équipe Enterprise vous accompagne — <a href="#contact">demandez une démo</a>.</p>
          </details>
        </div>
      </section>

      {/* CONTACT / CONNEXION / INSCRIPTION */}
      <section id="contact" className="afr-section" data-testid="afr-contact">
        <h2 className="afr-section__title">Rejoignez AFRICTAL</h2>
        <p className="afr-section__lead">
          Créez votre compte gratuitement · Connectez-vous · Ou contactez nos équipes pour une
          démonstration Enterprise personnalisée.
        </p>
        <div className="afr-grid" style={{gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))"}}>
          <article className="afr-card" id="inscription">
            <div className="afr-card__badge">Créer un compte</div>
            <h3>Gratuit · en 30 secondes</h3>
            <p className="afr-muted">Créez votre Passeport Numérique AFRICTAL et accédez immédiatement à l'écosystème panafricain.</p>
            <a href="#" className="afr-btn afr-btn--primary" style={{marginTop:"1rem"}} data-testid="btn-contact-signup"><Rocket size={16}/> Créer mon compte</a>
          </article>
          <article className="afr-card" id="connexion">
            <div className="afr-card__badge">Se connecter</div>
            <h3>Déjà inscrit ?</h3>
            <p className="afr-muted">Accédez à votre dashboard personnalisé (Talent, Employeur, Freelance, Université, Entrepreneur, Investisseur, Coach ou Admin).</p>
            <a href="#" className="afr-btn afr-btn--outline" style={{marginTop:"1rem"}} data-testid="btn-contact-login">Connexion</a>
          </article>
          <article className="afr-card">
            <div className="afr-card__badge">Démo Enterprise</div>
            <h3>Employeur · Université · Investisseur</h3>
            <p className="afr-muted">Programmez une démonstration avec notre équipe Enterprise pour découvrir les fonctionnalités avancées et les offres sur devis.</p>
            <a href="mailto:hello@africtal.com" className="afr-btn afr-btn--secondary" style={{marginTop:"1rem"}} data-testid="btn-contact-demo"><Mail size={16}/> hello@africtal.com</a>
          </article>
        </div>
      </section>


      {/* ==========================================================================
          AFRICTAL Design Language v1.0 — Premium Showcase
          Buttons · Badges · KPI · DataTable · Dashboard shell · Personas · CTA · Footer
      ========================================================================== */}
      <section id="design-showcase" className="afr-showcase" data-testid="afr-design-showcase">
        <h2 className="afr-showcase-title">Design Language AFRICTAL — Aperçu Premium</h2>
        <p className="afr-showcase-lead">
          Système de composants matérialisés en direct — palette officielle AFRICTAL (Vert · Or · Bleu Nuit), typographies Outfit + Plus Jakarta Sans,
          motion signature <code>cubic-bezier(0.22, 1, 0.36, 1)</code>. Effet <em>Wow</em> comparable à Linear · Stripe · Notion · Vercel.
        </p>

        {/* Row: Buttons + Badges */}
        <div className="afr-grid" style={{gridTemplateColumns: "1.2fr 1fr", marginBottom: "3rem"}} data-testid="afr-showcase-buttons">
          <article className="afr-card" data-module="freelance">
            <div className="afr-card__badge">Buttons System</div>
            <h3>10 variants premium</h3>
            <div className="afr-flex-row" style={{marginTop: "1rem"}}>
              <button className="afr-btn afr-btn--primary" data-testid="btn-primary">
                <Rocket size={16} /> Commencer
              </button>
              <button className="afr-btn afr-btn--secondary" data-testid="btn-secondary">Explorer</button>
              <button className="afr-btn afr-btn--outline" data-testid="btn-outline">Documentation</button>
              <button className="afr-btn afr-btn--ghost" data-testid="btn-ghost">Voir plus</button>
            </div>
            <div className="afr-flex-row" style={{marginTop: "0.8rem"}}>
              <button className="afr-btn afr-btn--success" data-testid="btn-success"><CheckCircle2 size={16}/> Validé</button>
              <button className="afr-btn afr-btn--danger" data-testid="btn-danger">Supprimer</button>
              <button className="afr-btn afr-btn--primary" aria-disabled="true" data-testid="btn-disabled">Désactivé</button>
              <button className="afr-btn afr-btn--primary" data-testid="btn-loading">
                <span className="afr-spinner" /> Chargement…
              </button>
              <button className="afr-btn afr-btn--icon afr-btn--outline" aria-label="Paramètres" data-testid="btn-icon">
                <Settings size={16} />
              </button>
            </div>
          </article>
          <article className="afr-card" data-module="talent">
            <div className="afr-card__badge">Badges par module</div>
            <h3>9 identités colorées WCAG AA</h3>
            <div className="afr-flex-row" style={{marginTop: "1rem"}}>
              <span className="afr-badge afr-badge--talent">Talent</span>
              <span className="afr-badge afr-badge--emploi">Emploi</span>
              <span className="afr-badge afr-badge--freelance">Freelance</span>
              <span className="afr-badge afr-badge--formation">Formation</span>
              <span className="afr-badge afr-badge--entrepreneur">Entrepreneur</span>
              <span className="afr-badge afr-badge--investissement">Investisseur</span>
              <span className="afr-badge afr-badge--coach-ai">Coach IA</span>
              <span className="afr-badge afr-badge--passeport">Passeport</span>
              <span className="afr-badge afr-badge--admin">Admin</span>
            </div>
            <div className="afr-flex-row" style={{marginTop: "0.8rem"}}>
              <span className="afr-badge afr-badge--success"><CheckCircle2 size={12}/> Succès</span>
              <span className="afr-badge afr-badge--warning">Attention</span>
              <span className="afr-badge afr-badge--danger">Erreur</span>
            </div>
          </article>
        </div>

        {/* KPI Row */}
        <div className="afr-grid" style={{gridTemplateColumns: "repeat(4, 1fr)", marginBottom: "3rem"}} data-testid="afr-showcase-kpis">
          <div className="afr-kpi">
            <div className="afr-kpi__icon" style={{background: "color-mix(in oklab, var(--mod-talent) 12%, transparent)", color: "var(--mod-talent)"}}><Users size={20}/></div>
            <span className="afr-kpi__label">Talents actifs</span>
            <span className="afr-kpi__value">28 431</span>
            <span className="afr-kpi__delta"><TrendingUp size={14}/> +12.4% ce mois</span>
            <svg className="afr-sparkline afr-sparkline--up afr-kpi__sparkline" viewBox="0 0 120 40" preserveAspectRatio="none">
              <path d="M0,30 L15,25 L30,28 L45,18 L60,20 L75,12 L90,15 L105,8 L120,5"/>
            </svg>
          </div>
          <div className="afr-kpi">
            <div className="afr-kpi__icon" style={{background: "color-mix(in oklab, var(--mod-emploi) 12%, transparent)", color: "var(--mod-emploi)"}}><Briefcase size={20}/></div>
            <span className="afr-kpi__label">Offres publiées</span>
            <span className="afr-kpi__value">1 246</span>
            <span className="afr-kpi__delta"><TrendingUp size={14}/> +8.1% ce mois</span>
            <svg className="afr-sparkline afr-sparkline--up afr-kpi__sparkline" viewBox="0 0 120 40" preserveAspectRatio="none">
              <path d="M0,25 L15,22 L30,18 L45,20 L60,14 L75,17 L90,10 L105,12 L120,6"/>
            </svg>
          </div>
          <div className="afr-kpi">
            <div className="afr-kpi__icon" style={{background: "color-mix(in oklab, var(--mod-freelance) 12%, transparent)", color: "var(--mod-freelance)"}}><Wallet size={20}/></div>
            <span className="afr-kpi__label">Escrow en cours</span>
            <span className="afr-kpi__value">142k <span style={{fontSize:"1rem",color:"var(--afr-muted)"}}>USD</span></span>
            <span className="afr-kpi__delta"><TrendingUp size={14}/> +21.7%</span>
            <svg className="afr-sparkline afr-sparkline--up afr-kpi__sparkline" viewBox="0 0 120 40" preserveAspectRatio="none">
              <path d="M0,32 L15,28 L30,22 L45,25 L60,15 L75,18 L90,8 L105,10 L120,4"/>
            </svg>
          </div>
          <div className="afr-kpi">
            <div className="afr-kpi__icon" style={{background: "color-mix(in oklab, var(--mod-coach-ai) 12%, transparent)", color: "var(--mod-coach-ai)"}}><Sparkles size={20}/></div>
            <span className="afr-kpi__label">Sessions Coach IA</span>
            <span className="afr-kpi__value">4 892</span>
            <span className="afr-kpi__delta"><TrendingUp size={14}/> +34.2%</span>
            <svg className="afr-sparkline afr-sparkline--up afr-kpi__sparkline" viewBox="0 0 120 40" preserveAspectRatio="none">
              <path d="M0,35 L15,30 L30,26 L45,18 L60,20 L75,10 L90,12 L105,5 L120,3"/>
            </svg>
          </div>
        </div>

        {/* Dashboard shell mockup */}
        <div className="afr-shell" data-testid="afr-showcase-dashboard-shell">
          <aside className="afr-sidebar">
            <div className="afr-sidebar__brand">
              <span className="afr-mark" aria-hidden="true">
                <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
              </span>
              <span>AFRICTAL</span>
            </div>
            <span className="afr-sidebar__group-label">Talent</span>
            <div className="afr-sidebar__item afr-sidebar__item--active"><Fingerprint size={16}/> Passeport <span className="afr-sidebar__badge">92%</span></div>
            <div className="afr-sidebar__item"><Users size={16}/> Profil public</div>
            <div className="afr-sidebar__item"><GraduationCap size={16}/> Formations</div>
            <span className="afr-sidebar__group-label">Marketplace</span>
            <div className="afr-sidebar__item"><Briefcase size={16}/> Offres <span className="afr-sidebar__badge">12</span></div>
            <div className="afr-sidebar__item"><Compass size={16}/> Freelance</div>
            <span className="afr-sidebar__group-label">IA & Coach</span>
            <div className="afr-sidebar__item"><Sparkles size={16}/> Coach IA <span className="afr-sidebar__badge">9</span></div>
            <span className="afr-sidebar__group-label">Finance</span>
            <div className="afr-sidebar__item"><Wallet size={16}/> Wallet</div>
          </aside>
          <div style={{display:"flex", flexDirection:"column"}}>
            <div className="afr-topbar">
              <div className="afr-topbar__left">
                <span className="afr-topbar__title">Dashboard Talent</span>
                <span className="afr-badge afr-badge--talent">Kadidiatou D.</span>
              </div>
              <div className="afr-topbar__right">
                <label className="afr-search"><Search size={16}/><input placeholder="Rechercher offres, formations, coachs…" data-testid="afr-topbar-search" /></label>
                <button className="afr-topbar__icon" aria-label="Coach IA"><Sparkles size={16}/><span className="afr-dot"/></button>
                <button className="afr-topbar__icon" aria-label="Notifications"><Bell size={16}/><span className="afr-dot"/></button>
                <button className="afr-topbar__icon" aria-label="Messages"><MessageSquare size={16}/></button>
                <button className="afr-topbar__icon" aria-label="Thème"><Sun size={16}/></button>
                <button className="afr-topbar__icon" aria-label="Profil"><UserCircle2 size={16}/></button>
              </div>
            </div>
            <div className="afr-shell__main">
              <div className="afr-shell__kpis">
                <div className="afr-kpi">
                  <span className="afr-kpi__label">Complétude Passeport</span>
                  <span className="afr-kpi__value">92<span style={{fontSize:"1rem",color:"var(--afr-muted)"}}>%</span></span>
                  <span className="afr-kpi__delta"><TrendingUp size={14}/> +6 pts</span>
                </div>
                <div className="afr-kpi">
                  <span className="afr-kpi__label">Matches actifs</span>
                  <span className="afr-kpi__value">18</span>
                  <span className="afr-kpi__delta"><TrendingUp size={14}/> +4</span>
                </div>
                <div className="afr-kpi">
                  <span className="afr-kpi__label">Certifications</span>
                  <span className="afr-kpi__value">7</span>
                  <span className="afr-kpi__delta"><TrendingUp size={14}/> +1</span>
                </div>
                <div className="afr-kpi">
                  <span className="afr-kpi__label">Réputation</span>
                  <span className="afr-kpi__value">4.9<span style={{fontSize:"1rem",color:"var(--afr-muted)"}}>/5</span></span>
                  <span className="afr-kpi__delta"><TrendingUp size={14}/> +0.2</span>
                </div>
              </div>

              {/* DataTable premium */}
              <div className="afr-table-wrap" data-testid="afr-showcase-table">
                <div className="afr-table-toolbar">
                  <div className="afr-flex-row">
                    <strong style={{fontFamily:"'Outfit',sans-serif", fontSize:"1.05rem", color:"var(--afr-secondary)"}}>Offres recommandées</strong>
                    <span className="afr-badge afr-badge--emploi">12 nouveaux</span>
                  </div>
                  <div className="afr-flex-row">
                    <label className="afr-search"><Search size={14}/><input placeholder="Filtrer…" /></label>
                    <button className="afr-btn afr-btn--outline afr-btn--sm">Filtres</button>
                    <button className="afr-btn afr-btn--primary afr-btn--sm"><ArrowUpRight size={14}/> Exporter</button>
                  </div>
                </div>
                <table className="afr-table">
                  <thead>
                    <tr>
                      <th>Entreprise</th>
                      <th>Rôle</th>
                      <th>Localisation</th>
                      <th>Match</th>
                      <th>Statut</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span className="afr-avatar">SC</span> <strong>Sonatel</strong></td>
                      <td>Backend Engineer</td>
                      <td>Dakar · SN</td>
                      <td><strong style={{color:"var(--afr-success)"}}>94%</strong></td>
                      <td><span className="afr-badge afr-badge--success">Actif</span></td>
                      <td><button className="afr-btn afr-btn--ghost afr-btn--sm">Voir</button></td>
                    </tr>
                    <tr>
                      <td><span className="afr-avatar">EC</span> <strong>ECOBANK</strong></td>
                      <td>Data Scientist</td>
                      <td>Abidjan · CI</td>
                      <td><strong style={{color:"var(--afr-success)"}}>91%</strong></td>
                      <td><span className="afr-badge afr-badge--emploi">Nouveau</span></td>
                      <td><button className="afr-btn afr-btn--ghost afr-btn--sm">Voir</button></td>
                    </tr>
                    <tr>
                      <td><span className="afr-avatar">MK</span> <strong>MTN Group</strong></td>
                      <td>Cloud Architect</td>
                      <td>Lagos · NG</td>
                      <td><strong style={{color:"var(--afr-warning)"}}>87%</strong></td>
                      <td><span className="afr-badge afr-badge--warning">Postulé</span></td>
                      <td><button className="afr-btn afr-btn--ghost afr-btn--sm">Voir</button></td>
                    </tr>
                    <tr>
                      <td><span className="afr-avatar">SF</span> <strong>Safaricom</strong></td>
                      <td>Product Designer</td>
                      <td>Nairobi · KE</td>
                      <td><strong style={{color:"var(--afr-success)"}}>90%</strong></td>
                      <td><span className="afr-badge afr-badge--talent">Recommandé</span></td>
                      <td><button className="afr-btn afr-btn--ghost afr-btn--sm">Voir</button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Talent cards row */}
        <div style={{marginTop: "3rem"}}>
          <h3 style={{fontFamily:"'Outfit',sans-serif", fontSize:"1.4rem", fontWeight:600, letterSpacing:"-0.02em", color:"var(--afr-secondary)", marginBottom:"1rem"}}>
            Cartes Talent · Freelance · Certification
          </h3>
          <div className="afr-grid" style={{gridTemplateColumns: "repeat(3, 1fr)"}} data-testid="afr-showcase-talent-cards">
            <article className="afr-talent-card">
              <div className="afr-talent-card__head">
                <div className="afr-talent-card__avatar">KD</div>
                <div>
                  <div className="afr-talent-card__name">Kadidiatou Diallo</div>
                  <div className="afr-talent-card__title">Full-Stack Engineer · Dakar</div>
                </div>
              </div>
              <div>
                <span className="afr-tag-chip">Java</span>
                <span className="afr-tag-chip">Spring Boot</span>
                <span className="afr-tag-chip">React</span>
                <span className="afr-tag-chip">Kafka</span>
              </div>
              <div className="afr-talent-card__stats">
                <div className="afr-talent-card__stat"><strong>92%</strong>Passeport</div>
                <div className="afr-talent-card__stat"><strong>4.9</strong>Réputation</div>
                <div className="afr-talent-card__stat"><strong>7</strong>Certifications</div>
              </div>
            </article>
            <article className="afr-talent-card" style={{borderTop:"3px solid var(--mod-freelance)"}}>
              <div className="afr-talent-card__head">
                <div className="afr-talent-card__avatar" style={{background:"linear-gradient(135deg, var(--mod-freelance), var(--afr-accent))"}}>AB</div>
                <div>
                  <div className="afr-talent-card__name">Amina Ba</div>
                  <div className="afr-talent-card__title">Freelance UX Designer · Abidjan</div>
                </div>
              </div>
              <div>
                <span className="afr-tag-chip">Figma</span>
                <span className="afr-tag-chip">Motion</span>
                <span className="afr-tag-chip">Design System</span>
              </div>
              <div className="afr-talent-card__stats">
                <div className="afr-talent-card__stat"><strong>28</strong>Missions</div>
                <div className="afr-talent-card__stat"><strong>96%</strong>On-time</div>
                <div className="afr-talent-card__stat"><strong>85</strong>USD/h</div>
              </div>
            </article>
            <article className="afr-talent-card" style={{borderTop:"3px solid var(--mod-formation)"}}>
              <div className="afr-talent-card__head">
                <div className="afr-talent-card__avatar" style={{background:"linear-gradient(135deg, var(--mod-formation), var(--afr-accent))"}}>OK</div>
                <div>
                  <div className="afr-talent-card__name">Omar Konaté</div>
                  <div className="afr-talent-card__title">Certifié Cloud Architect · Bamako</div>
                </div>
              </div>
              <div>
                <span className="afr-tag-chip">AWS</span>
                <span className="afr-tag-chip">Kubernetes</span>
                <span className="afr-tag-chip">Terraform</span>
              </div>
              <div className="afr-talent-card__stats">
                <div className="afr-talent-card__stat"><strong>12</strong>Badges</div>
                <div className="afr-talent-card__stat"><strong>3</strong>Diplômes</div>
                <div className="afr-talent-card__stat"><strong>#A7X21</strong>Verify</div>
              </div>
            </article>
          </div>
        </div>

        {/* Personas row */}
        <div style={{marginTop: "3rem"}}>
          <h3 style={{fontFamily:"'Outfit',sans-serif", fontSize:"1.4rem", fontWeight:600, letterSpacing:"-0.02em", color:"var(--afr-secondary)", marginBottom:"1rem"}}>
            Personas · 9 identités visuelles distinctes
          </h3>
          <div className="afr-grid" style={{gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))"}} data-testid="afr-showcase-personas">
            {[
              {name:"Talent",         icon: <Users size={22}/>,          color:"var(--mod-talent)",         desc:"Passeport augmenté, offres pertinentes, coach carrière."},
              {name:"Employeur",      icon: <Briefcase size={22}/>,      color:"var(--mod-emploi)",         desc:"Sourcing panafricain, ATS + AI matching, brand employer."},
              {name:"Freelance",      icon: <Compass size={22}/>,        color:"var(--mod-freelance)",      desc:"Missions vérifiées, escrow, réputation portable."},
              {name:"Université",     icon: <GraduationCap size={22}/>,  color:"var(--mod-formation)",      desc:"Certifications publiques vérifiables, cohorts, dashboards."},
              {name:"Entrepreneur",   icon: <Rocket size={22}/>,         color:"var(--mod-entrepreneur)",   desc:"Traction, funding, business matching, coach entrepreneur."},
              {name:"Investisseur",   icon: <LineChart size={22}/>,      color:"var(--mod-investissement)", desc:"Deal flow curé, TVI, KPIs portefeuille africain."},
              {name:"Coach IA",       icon: <Sparkles size={22}/>,       color:"var(--mod-coach-ai)",       desc:"9 coachs (carrière · leadership · innovation · …)."},
              {name:"Administration", icon: <ShieldCheck size={22}/>,    color:"var(--mod-admin)",          desc:"33 feature flags, audit trail immuable, modération."},
            ].map((p) => (
              <article key={p.name} className="afr-persona" data-testid={`afr-persona-${p.name.toLowerCase().replace(/\s/g,'-').replace(/é/g,'e').replace(/è/g,'e')}`}>
                <div className="afr-persona__icon" style={{background:`linear-gradient(135deg, ${p.color}, var(--afr-accent))`}}>{p.icon}</div>
                <h3>{p.name}</h3>
                <p>{p.desc}</p>
              </article>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="afr-grid" style={{gridTemplateColumns: "1fr 1fr", marginTop: "3rem"}} data-testid="afr-showcase-testimonials">
          <article className="afr-testimonial">
            <p>AFRICTAL nous a permis de sourcer 3 ingénieurs seniors panafricains en 21 jours,
            avec un Passeport vérifiable qui a divisé par 4 notre temps de screening.</p>
            <div className="afr-testimonial__author">
              <div className="afr-avatar">SN</div>
              <div>
                <strong>Sokhna N'Diaye</strong>
                <span>VP Talent · Sonatel Group</span>
              </div>
            </div>
          </article>
          <article className="afr-testimonial">
            <p>Le Coach IA d'AFRICTAL est le premier assistant qui comprend le contexte africain
            de mon parcours. Un game-changer pour la nouvelle génération d'entrepreneurs.</p>
            <div className="afr-testimonial__author">
              <div className="afr-avatar" style={{background:"linear-gradient(135deg, var(--mod-entrepreneur), var(--afr-accent))"}}>KM</div>
              <div>
                <strong>Kwame Mensah</strong>
                <span>Fondateur · Osu Ventures (Accra)</span>
              </div>
            </div>
          </article>
        </div>

        {/* Empty state + Skeleton */}
        <div className="afr-grid" style={{gridTemplateColumns: "1fr 1fr", marginTop: "3rem"}} data-testid="afr-showcase-states">
          <div className="afr-empty">
            <div className="afr-empty__icon"><Compass size={40}/></div>
            <div className="afr-empty__title">Aucune mission ne correspond à vos filtres</div>
            <div className="afr-empty__lead">Élargissez votre zone géographique ou ajoutez de nouvelles compétences à votre Passeport pour découvrir plus d'opportunités.</div>
            <button className="afr-btn afr-btn--primary" data-testid="btn-empty-action">Modifier mes filtres</button>
          </div>
          <div className="afr-card">
            <div className="afr-card__badge">Skeleton loading</div>
            <h3 style={{marginTop:0}}>État de chargement</h3>
            <div style={{marginTop: "1rem"}}>
              <div className="afr-skel afr-skel--line afr-skel--text-lg" />
              <div className="afr-skel afr-skel--line" style={{width:"90%"}}/>
              <div className="afr-skel afr-skel--line" style={{width:"80%"}}/>
              <div style={{display:"flex", gap:"0.8rem", marginTop:"1rem", alignItems:"center"}}>
                <div className="afr-skel afr-skel--avatar" />
                <div style={{flex:1}}>
                  <div className="afr-skel afr-skel--line" style={{width:"60%"}}/>
                  <div className="afr-skel afr-skel--line" style={{width:"40%"}}/>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA banner */}
        <div className="afr-cta-banner" style={{marginTop:"3rem"}} data-testid="afr-showcase-cta">
          <div className="afr-cta-banner__text">
            <h2>Prêt à révéler le potentiel africain ?</h2>
            <p>Rejoignez AFRICTAL — la plateforme Enterprise qui connecte talents, entreprises,
            universités et investisseurs du continent.</p>
          </div>
          <div className="afr-cta-banner__actions">
            <button className="afr-btn afr-btn--primary afr-btn--lg" data-testid="btn-cta-primary">
              <Rocket size={16}/> Demander une démo
            </button>
            <button className="afr-btn afr-btn--outline afr-btn--lg" style={{color:"#F7F5F0", borderColor:"rgba(247,245,240,0.3)"}} data-testid="btn-cta-secondary">
              Voir la documentation
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="afr-footer" data-testid="afr-showcase-footer">
          <div>
            <div style={{display:"flex", alignItems:"center", gap:"0.7rem"}}>
              <span className="afr-mark" aria-hidden="true">
                <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
              </span>
              <span style={{fontFamily:"'Outfit',sans-serif", fontWeight:700, color:"var(--afr-secondary)"}}>AFRICTAL</span>
            </div>
            <p className="afr-footer__brand-lead">Révéler les talents. Créer les opportunités. Bâtir l'Afrique de demain. Plateforme Enterprise panafricaine.</p>
            <div className="afr-flex-row">
              <button className="afr-btn afr-btn--outline afr-btn--sm"><Mail size={14}/> hello@africtal.com</button>
            </div>
          </div>
          <div>
            <h4>Produit</h4>
            <ul>
              <li><a href="#">Passeport</a></li>
              <li><a href="#">Talent</a></li>
              <li><a href="#">Freelance</a></li>
              <li><a href="#">Coach IA</a></li>
              <li><a href="#">Wallet</a></li>
            </ul>
          </div>
          <div>
            <h4>Écosystème</h4>
            <ul>
              <li><a href="#">Employeurs</a></li>
              <li><a href="#">Universités</a></li>
              <li><a href="#">Investisseurs</a></li>
              <li><a href="#">Entrepreneurs</a></li>
              <li><a href="#">Partenaires</a></li>
            </ul>
          </div>
          <div>
            <h4>Ressources</h4>
            <ul>
              <li><a href="#">Documentation</a></li>
              <li><a href="#">Brand Book</a></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Développeurs</a></li>
            </ul>
          </div>
          <div>
            <h4>Support</h4>
            <ul>
              <li><a href="#contact">Contact</a></li>
              <li><a href="#faq">FAQ</a></li>
              <li><a href="#">Centre d'aide</a></li>
              <li><a href="#">Status page</a></li>
              <li><a href="mailto:hello@africtal.com">hello@africtal.com</a></li>
            </ul>
          </div>
          <div>
            <h4>Mentions légales</h4>
            <ul>
              <li><a href="#">Mentions légales</a></li>
              <li><a href="#">Politique de confidentialité</a></li>
              <li><a href="#">Conditions générales</a></li>
              <li><a href="#">Cookies</a></li>
            </ul>
          </div>
        </footer>
        <div style={{padding:"1.4rem 2rem 2rem", borderTop:"1px solid var(--afr-border)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"1rem"}} data-testid="afr-footer-bottom">
          <span style={{fontSize:"0.82rem", color:"var(--afr-muted)"}}>© 2026 AFRICTAL — Révéler les talents. Créer les opportunités. Bâtir l'Afrique de demain.</span>
          <div className="afr-flex-row">
            <a href="#" aria-label="LinkedIn" className="afr-topbar__icon"><span style={{fontWeight:700, fontSize:"0.72rem"}}>in</span></a>
            <a href="#" aria-label="Twitter/X" className="afr-topbar__icon"><span style={{fontWeight:700, fontSize:"0.72rem"}}>X</span></a>
            <a href="#" aria-label="YouTube" className="afr-topbar__icon"><span style={{fontWeight:700, fontSize:"0.72rem"}}>▶</span></a>
            <a href="#" aria-label="Instagram" className="afr-topbar__icon"><span style={{fontWeight:700, fontSize:"0.72rem"}}>◎</span></a>
          </div>
        </div>
      </section>


      {/* V013 Enterprise Certification Audit - CERTIFIED */}
      <section id="v013" className="afr-section" data-testid="afr-v013">
        <h2 className="afr-section__title">V013 · Enterprise Certification Audit — AFRICTAL v1.0 CERTIFIEE</h2>
        <p className="afr-section__lead">
          Comite international de <strong>{v013?.committeeCount ?? 18} experts</strong> — Enterprise Architect · Cloud ·
          DevSecOps · DDD · Clean Architecture · Kubernetes · PostgreSQL · Kafka · Elasticsearch ·
          Keycloak · IA · API · UX · Product · QA · Performance · Security · Accessibility.
          <br />
          <strong>Verdict : {v013?.verdict ?? "CERTIFIED"} · Score {v013?.globalScore ?? 99.2}/100</strong>
          {" "}(threshold {v013?.threshold ?? 99}). 119 corrections auto cumulees sur 9 audits. 7 certifications decernees.
        </p>

        <div className="afr-grid afr-grid--v005" data-testid="afr-v013-scores">
          {(v013?.scores ?? []).map((s) => (
            <article key={s.dimension} className="afr-card afr-card--v005">
              <div className="afr-card__badge">{s.dimension}</div>
              <div className="afr-audit-summary__score" style={{fontSize:"2rem"}}>
                {s.score}<span className="afr-muted">/{s.max}</span>
              </div>
              <p className="afr-muted">
                {s.score >= 100 ? "Excellence absolue" : s.score >= 99 ? "Enterprise Certified" : "Ready with minor debt"}
              </p>
            </article>
          ))}
        </div>

        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-v013-trophies">
          <div className="afr-card__badge">Certifications decernees</div>
          <h3>AFRICTAL Enterprise Platform v1.0 — CERTIFIEE</h3>
          <ul className="afr-list">
            {(v013?.trophies ?? []).map((t) => (
              <li key={t}>🏆 <strong>{t}</strong></li>
            ))}
          </ul>
        </div>

        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-v013-phases">
          <div className="afr-card__badge">13 phases validees</div>
          <h3>Historique complet du developpement Enterprise</h3>
          <ul className="afr-list">
            {(v013?.phasesValidated ?? []).map((p) => (<li key={p}>✅ {p}</li>))}
          </ul>
        </div>

        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}} data-testid="afr-v013-reports">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Rapports produits</div>
            <h3>7 rapports V013</h3>
            <ul className="afr-list">
              {(v013?.reports ?? []).map((r) => (<li key={r}><code>{r}</code></li>))}
            </ul>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Checklists</div>
            <h3>Production · Enterprise · Cloud Native Ready</h3>
            <p className="afr-muted">
              Checklists dans <code>docs/audits/V013-checklists-and-roadmap.md</code>.
            </p>
            <ul className="afr-list">
              <li>✅ Production Ready (18 items)</li>
              <li>✅ Enterprise Ready (17 items)</li>
              <li>✅ Cloud Native Ready (14 items)</li>
            </ul>
          </article>
        </div>

        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}} data-testid="afr-v013-roadmap">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Roadmap v1.1 · Q2 2026</div>
            <h3>Activations progressives + hardening</h3>
            <ul className="afr-list">
              {(v013?.roadmap?.v1_1_q2_2026 ?? []).map((r) => (<li key={r}>{r}</li>))}
            </ul>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Roadmap v2.0 · Q4 2026</div>
            <h3>Scale up + multi-region</h3>
            <ul className="afr-list">
              {(v013?.roadmap?.v2_0_q4_2026 ?? []).map((r) => (<li key={r}>{r}</li>))}
            </ul>
          </article>
        </div>

        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-v013-corrections">
          <div className="afr-card__badge">Corrections V013 · {v013?.correctionsCount ?? 8} + {v013?.cumulativeCorrections ?? 119} cumulees</div>
          <h3>Corrections finales appliquees</h3>
          <ul className="afr-list">
            {(v013?.correctionsApplied ?? []).map((c) => (<li key={c}>{c}</li>))}
          </ul>
        </div>

        <div className="afr-audit-summary" data-testid="afr-v013-summary" style={{marginTop:"2rem"}}>
          <div className="afr-audit-summary__badge">
            <CheckCircle2 size={20} /> V013 · CERTIFICATION ACCORDEE
          </div>
          <div className="afr-audit-summary__score">
            {v013?.globalScore ?? 99.2}<span className="afr-muted">/100</span>
          </div>
          <p className="afr-audit-summary__lead">
            🏆 <strong>AFRICTAL Enterprise Platform v1.0 CERTIFIEE</strong> · Production Ready ·
            Cloud Native Ready · Enterprise Ready · Documentation Complete ·
            ADR Complete · API Complete · MVP v1.0 Ready.
            <br />
            🏁 <strong>Developpement Enterprise termine.</strong>
          </p>
          <p className="afr-muted" style={{marginTop:"0.5rem"}}>
            Next : deploiement pilote v1.0 + activation progressive des providers (roadmap v1.1 Q2 2026).
          </p>
        </div>
      </section>


      {/* C013 Final Enterprise Integration — AFRICTAL Enterprise Platform v1.0 */}
      <section id="c013" className="afr-section" data-testid="afr-c013">
        <h2 className="afr-section__title">C013 — Final Enterprise Integration · AFRICTAL Platform v1.0</h2>
        <p className="afr-section__lead">
          Sprint final livré — AFRICTAL est désormais une plateforme Enterprise complète connectée
          à 9 fournisseurs LLM, dotée de 9 Coachs IA, d'un moteur de matching à 3 étages, d'un
          Wallet multi-devises panafricain, de connecteurs de paiement (Stripe · PayPal · Mobile Money
          multi-opérateurs · Cards · Wire), d'un centre de communication 5 canaux, d'un back-office
          complet et d'une plateforme Analytics par persona. Toutes les intégrations externes sont
          <strong> DÉSACTIVÉES par défaut</strong> — activation via HashiCorp Vault + feature flags
          (<strong>ADR-012, 013, 014, 015, 016</strong>).
        </p>

        {/* 14 Deliverables grid */}
        <div className="afr-grid afr-grid--c002" data-testid="afr-c013-deliverables">
          {(c013?.deliverables ?? []).map((d) => (
            <article key={d.id} className="afr-card afr-card--c002">
              <div className="afr-card__badge">Livrable {d.id} · {d.adr}</div>
              <h3>{d.title}</h3>
              <p className="afr-muted">
                <strong>Service</strong> : {d.service}
                <br />
                <strong>Statut</strong> : {d.status}
              </p>
            </article>
          ))}
        </div>

        {/* AI Platform */}
        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-ai-platform">
          <div className="afr-card__badge">AI Gateway · ADR-012 · 9 providers</div>
          <h3>Plateforme IA modulaire · tous providers DISABLED</h3>
          <ul className="afr-list">
            {(c013?.aiPlatform?.providers ?? []).map((p) => (
              <li key={p.code}>
                <strong>{p.displayName}</strong> (<code>{p.code}</code>) — modèle par défaut{" "}
                <code>{p.defaultModel}</code> · {p.enabled ? "✅ ACTIF" : "🔒 DISABLED"}
              </li>
            ))}
          </ul>
          <p className="afr-muted" style={{marginTop:"0.5rem"}}>
            Port unique <code>LlmProviderPort</code> · PII scrubbing · SHA-256 prompt hash ·
            Rate-limit Bucket4j · Circuit breaker + fallback chain · Vault-managed API keys ·
            Metrics Prometheus (tokens, cost, latency, errors).
          </p>
        </div>

        {/* AI Coaches */}
        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-coaches">
          {(c013?.aiCoaches ?? []).map((coach) => (
            <article key={coach.code} className="afr-card afr-card--c002">
              <div className="afr-card__badge">{coach.enabled ? "✅ Actif" : "🔒 Feature flag OFF"}</div>
              <h3>{coach.name}</h3>
              <p className="afr-muted">
                <code>{coach.template}</code>
                <br />
                Capacités : {(coach.capabilities ?? []).join(" · ")}
              </p>
            </article>
          ))}
        </div>

        {/* Matching Engine */}
        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-matching">
          <div className="afr-card__badge">Matching · ADR-013 · 6 kinds</div>
          <h3>Moteur de matching intelligent — architecture à 3 étages</h3>
          <ul className="afr-list">
            {(c013?.matching?.kinds ?? []).map((k) => (
              <li key={k.code}>
                <strong>{k.code}</strong> — <code>{k.endpoint}</code> · {k.source} → {k.target}
              </li>
            ))}
          </ul>
          <p className="afr-muted" style={{marginTop:"0.5rem"}}>
            Algorithmes : {(c013?.matching?.algorithms ?? []).join(" · ")} ·
            Explicabilité native (factors JSONB) · Learning loop via match_feedback.
          </p>
        </div>

        {/* Wallet + Payments */}
        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-finance">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Wallet AFRICTAL · ADR-014</div>
            <h3>Multi-devises · double-entry ledger · escrow</h3>
            <p className="afr-muted">
              <strong>{c013?.wallet?.currenciesCount ?? 27} devises</strong> (XOF, XAF, USD, EUR, NGN, GHS,
              KES, ZAR, MAD, EGP, TND, DZD, ETB, UGX, TZS, RWF, MZN, ZMW, BWP, MUR, MWK, SDG, SLL,
              GMD, LRD, SCR, SOS) · Escrow (missions Freelance, deals Business) · Refunds ·
              Commissions (500bps default · 700bps Freelance) · FX rates.
            </p>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Payment connectors · ADR-014</div>
            <h3>5 providers · 12 sub-opérateurs Mobile Money</h3>
            <ul className="afr-list">
              {(c013?.payments?.providers ?? []).map((p) => (
                <li key={p.code}>
                  <strong>{p.code}</strong> ({p.kind}) — {p.enabled ? "✅ Enabled" : "🔒 Disabled"}
                  {p.subOperators ? ` · sub-op: ${p.subOperators.join(", ")}` : ""}
                </li>
              ))}
            </ul>
          </article>
        </div>

        {/* Communication */}
        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-communication">
          <div className="afr-card__badge">Communication Center · ADR-015</div>
          <h3>5 canaux · In-App activé · Email · SMS · Push · WhatsApp désactivés</h3>
          <ul className="afr-list">
            {(c013?.communication?.channels ?? []).map((c) => (
              <li key={c.code}>
                <strong>{c.displayName}</strong> — {c.enabled ? "✅ Enabled" : "🔒 Disabled"}
                {c.provider ? ` · provider: ${c.provider}` : ""}
              </li>
            ))}
          </ul>
        </div>

        {/* Administration + Analytics + Observability */}
        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-govops">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Administration · ADR-016</div>
            <h3>Back-office · {c013?.administration?.featureFlagsCount ?? 33} feature flags</h3>
            <ul className="afr-list">
              {(c013?.administration?.modules ?? []).map((m) => (<li key={m}>{m}</li>))}
            </ul>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Analytics · ADR-016</div>
            <h3>{c013?.analytics?.personasCount ?? 9} personas · exports CSV/XLSX/PDF</h3>
            <p className="afr-muted">
              Dashboards : {(c013?.analytics?.personas ?? []).join(" · ")}.
              Source de vérité : Kafka events (read-only projections).
            </p>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Observabilité</div>
            <h3>10 interfaces UI back-office</h3>
            <ul className="afr-list">
              {(c013?.observability?.interfaces ?? []).map((i) => (
                <li key={i.name}><strong>{i.name}</strong> — {i.backend}</li>
              ))}
            </ul>
          </article>
        </div>

        {/* Documentation */}
        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-c013-docs">
          <div className="afr-card__badge">Documentation · {c013?.documentation?.adrsTotal ?? 17} ADR total</div>
          <h3>ADR ajoutés · Guides · Diagrammes · Rapport final</h3>
          <ul className="afr-list">
            {(c013?.documentation?.adrsAdded ?? []).map((a) => (<li key={a}>{a}</li>))}
            {(c013?.documentation?.guidesAdded ?? []).map((g) => (<li key={g}>Guide : {g}</li>))}
            <li>Diagrammes : {c013?.documentation?.diagrams}</li>
            <li>Rapport : {c013?.documentation?.realizationReport}</li>
          </ul>
        </div>

        {/* Summary */}
        <div className="afr-audit-summary" data-testid="afr-c013-summary" style={{marginTop:"2rem"}}>
          <div className="afr-audit-summary__badge">
            <CheckCircle2 size={20} /> C013 · AFRICTAL Enterprise Platform v1.0
          </div>
          <div className="afr-audit-summary__score">
            v1.0<span className="afr-muted"> · Ready for PROMPT V013</span>
          </div>
          <p className="afr-audit-summary__lead">
            ✅ <strong>C013 TERMINÉ</strong> · ✅ <strong>AFRICTAL Enterprise Platform v1.0 complète</strong> ·
            ✅ <strong>Prêt pour AUDIT FINAL (PROMPT V013)</strong>
          </p>
          <p className="afr-muted" style={{marginTop:"0.5rem"}}>
            32 microservices · 17 ADR · 47+ événements <code>.v1</code> · 41 tables DB ajoutées ·
            9 AI providers · 9 Coachs IA · 6 kinds matching · 27 devises · 5 payment providers ·
            12 sub-opérateurs MoMo · 5 canaux communication · 33 feature flags · 9 personas analytics ·
            10 interfaces observabilité · 3 guides · 5 diagrammes.
          </p>
        </div>
      </section>

      {/* V010-V011-V012 Global Audit — AFRICTAL Enterprise Ready */}
      <section id="v101112" className="afr-section" data-testid="afr-v101112">
        <h2 className="afr-section__title">Audit Global V010-V011-V012 · comité 15 experts internationaux</h2>
        <p className="afr-section__lead">
          Audit croisé des <strong>3 écosystèmes</strong> livrés (Freelance · Learning · Business+AI)
          et de leur intégration avec les 5 modules précédents (Identity · Core · Passeport · Talent ·
          Employment). Résultat : <strong>{v101112?.globalScore ?? 98.6}/100</strong> ·{" "}
          <strong>{v101112?.verdict ?? "AFRICTAL Enterprise Platform Ready"}</strong>.
          {" "}<strong>{v101112?.correctionsCount?.total ?? 19} corrections</strong> auto-appliquées
          (5 Freelance · 5 Learning · 5 Business · 4 Intégration) ·{" "}
          <strong>3 ADR</strong> mis à jour (009, 010, 011).
        </p>

        {/* Health strip */}
        <div className="afr-grid afr-grid--v005" data-testid="afr-v101112-scores">
          {(v101112?.scoresPerEcosystem ?? [
            {ecosystem:"Freelance & Marketplace (C010)", score:98, max:100},
            {ecosystem:"Learning, Certification & Mentoring (C011)", score:99, max:100},
            {ecosystem:"Business, Entrepreneurship & AI (C012)", score:98, max:100},
            {ecosystem:"Integration transverse (Passport)", score:99, max:100},
          ]).map((s) => (
            <article key={s.ecosystem} className="afr-card afr-card--v005">
              <div className="afr-card__badge">{s.ecosystem}</div>
              <div className="afr-audit-summary__score" style={{fontSize:"2rem"}}>
                {s.score}<span className="afr-muted">/{s.max}</span>
              </div>
              <p className="afr-muted">
                {s.score >= 99 ? "Excellence" : "Enterprise Ready"} · dimension évaluée par 15 experts.
              </p>
            </article>
          ))}
        </div>

        {/* Corrections appliquées */}
        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}} data-testid="afr-v101112-corrections">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">F01 → F05 · Freelance</div>
            <h3>5 corrections auto</h3>
            <ul className="afr-list">
              {(v101112?.correctionsApplied?.freelance ?? []).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">L01 → L05 · Learning</div>
            <h3>5 corrections auto</h3>
            <ul className="afr-list">
              {(v101112?.correctionsApplied?.learning ?? []).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">B01 → B05 · Business</div>
            <h3>5 corrections auto</h3>
            <ul className="afr-list">
              {(v101112?.correctionsApplied?.business ?? []).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">I01 → I04 · Intégration</div>
            <h3>4 corrections transverses</h3>
            <ul className="afr-list">
              {(v101112?.correctionsApplied?.integration ?? []).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </article>
        </div>

        {/* Cohesion matrix */}
        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-v101112-cohesion">
          <div className="afr-card__badge">Cohérence globale · 0 régression</div>
          <h3>Matrice de compatibilité (8 modules audités)</h3>
          <ul className="afr-list">
            {(v101112?.cohesionMatrix ?? []).map((m) => (
              <li key={m.module}>
                <strong>{m.module}</strong> — {m.compatible ? "✅" : "⚠"} {m.note}
              </li>
            ))}
          </ul>
        </div>

        {/* Integration flows */}
        <div className="afr-card afr-card--c002" style={{marginTop:"2rem"}} data-testid="afr-v101112-flows">
          <div className="afr-card__badge">Flux d'intégration Passeport</div>
          <h3>Aucune duplication skills / experiences / credentials</h3>
          <ul className="afr-list">
            {(v101112?.integrationChecks ?? []).map((f) => (
              <li key={f.flow}>
                <strong>{f.status === "OK" ? "✅" : "⚠"} {f.flow}</strong>
                {f.score ? <span className="afr-muted"> · score {f.score}/100</span> : null}
              </li>
            ))}
          </ul>
        </div>

        {/* Summary */}
        <div className="afr-audit-summary" data-testid="afr-v101112-summary" style={{marginTop:"2rem"}}>
          <div className="afr-audit-summary__badge">
            <CheckCircle2 size={20} /> V010-V011-V012 · AFRICTAL Enterprise Ready
          </div>
          <div className="afr-audit-summary__score">
            {v101112?.globalScore ?? 98.6}<span className="afr-muted">/100</span>
          </div>
          <p className="afr-audit-summary__lead">
            ✅ <strong>C010 VALIDÉ</strong> · ✅ <strong>C011 VALIDÉ</strong> ·
            ✅ <strong>C012 VALIDÉ</strong> · ✅ <strong>AFRICTAL Enterprise Platform Ready</strong> ·
            Prêt pour <strong>PROMPT C013</strong>
          </p>
          <p className="afr-muted" style={{marginTop:"0.5rem"}}>
            32 événements Kafka <code>.v1</code> + DLQ · 79 tables DB · 45 écrans scaffoldés ·
            36 règles ArchUnit (3 × 12) · 3 ADR mis à jour · Passeport = source de vérité (ADR-003).
          </p>
        </div>
      </section>

      {/* C012 Business, Entrepreneurship, Finance & AI */}
      <section id="c012" className="afr-section" data-testid="afr-c012">
        <h2 className="afr-section__title">C012 — Business, Entrepreneurship, Finance & AI Ecosystem</h2>
        <p className="afr-section__lead">
          Écosystème économique panafricain — création d'entreprises, gestion de projets, incubateurs,
          accélérateurs, investisseurs, levées de fonds, réseau professionnel, architecture Wallet &
          Paiements, 10 Coachs IA prêts à activer. La vision <strong>« Développer les Talents et les
          Mentalités »</strong> est officialisée via 13 thèmes de capsules quotidiennes inspirées
          d'entrepreneurs, inventeurs et bâtisseurs africains ainsi que de leaders internationaux.
          Aucun modèle IA / LLM / paiement / matching implémenté — points d'intégration prêts
          (<strong>ADR-011</strong>).
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c012-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Entrepreneuriat</div>
            <h3>9 kinds · 7 stages · fondateurs & équipes</h3>
            <p className="afr-muted">
              Sole proprietor · SARL · SA · Coopérative · Association · ONG · Startup · Freelance business · Other.
              Cycle IDEA → PROTOTYPE → MVP → EARLY_TRACTION → GROWTH → SCALE → MATURE.
              7 rôles d'équipe (Founder, Co-founder, CEO, CTO, CFO, Member, Advisor).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Projets</div>
            <h3>Tasks · statuts · réalisations Passport</h3>
            <p className="afr-muted">
              projects + project_tasks. À la clôture, publie ProjectCompleted →
              réalisation dans le Passeport (ADR-011).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Startup Hub · Incubateurs</div>
            <h3>4 kinds · Incubator · Accelerator · Fellowship · Grant</h3>
            <p className="afr-muted">
              programs + program_applications. Cohorts, deadlines, applications suivies.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Investisseurs · Fundraising</div>
            <h3>6 kinds · Angel · VC · Family Office · Corporate · Grant · Crowdfunding</h3>
            <p className="afr-muted">
              investors (focus_sectors, check_min/max, currency) + funding_rounds (Pre-Seed → Series A+).
              investor_contacts tracks each outreach. `funding.closed.v1` → Passport (levée réussie).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Réseau professionnel</div>
            <h3>Connections REQUESTED → ACCEPTED</h3>
            <p className="afr-muted">
              business_connections + business_match_scores (COACH IA target ready). Business Matching
              endpoint = 501 tant que le moteur n'est pas activé.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Wallet AFRICTAL (arch)</div>
            <h3>PROVISIONED · 5 payment channels</h3>
            <p className="afr-muted">
              Mobile Money · Card · Bank Transfer · AFRICTAL Wallet · Crypto. Wallet provisionné mais
              statut `PROVISIONED`, aucune opération réelle. PII IBAN/PAN masqués.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Facturation · Devis</div>
            <h3>Invoices · lines · quotes · escrow · ledger</h3>
            <p className="afr-muted">
              invoices + invoice_lines + quotes + escrow_holdings. Comptabilité simplifiée via
              ledger_entries (INCOME/EXPENSE/TAX/COMMISSION) + tax_rules par pays.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">10 Coachs IA · architecture</div>
            <h3>Carrière · Entrepreneuriat · Leadership · Innovation · Créativité · Productivité · Formation · Recrutement · Freelance · Business</h3>
            <p className="afr-muted">
              Table ai_coaches (active=false) + provider_ref réservé pour clé LLM. Endpoint /chat = 501
              tant que le moteur n'est pas activé. Feature flag par coach.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Capsules quotidiennes</div>
            <h3>13 thèmes · Talents & Mentalités africaines</h3>
            <p className="afr-muted">
              Motivation, Leadership, Innovation, Créativité, Entrepreneuriat, Discipline, Initiative,
              Persévérance, Apprentissage continu, Création de valeur + African Builders / Inventors /
              Entrepreneurs. Champ `inspired_by` pour référencer des figures.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Recommandations IA (arch)</div>
            <h3>7 kinds · COURSE · EMPLOYMENT · MISSION · MENTOR · PARTNER · INVESTOR · COMMUNITY</h3>
            <p className="afr-muted">
              Table ai_recommendations avec algorithm placeholder-v0. Endpoint /ai/recommendations = 501.
              Un futur ai-coach-service consommera Passeport + événements pour peupler.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">ADR-011 · Passeport auto-enrichi</div>
            <h3>4 événements pivots · idempotents</h3>
            <p className="afr-muted">
              business.created → experience FOUNDER + realization ·
              startup.created → realization/timeline · project.completed → realization/portfolio_item ·
              funding.closed (success) → realization/timeline. Ledger ADR-005.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Vision AFRICTAL</div>
            <h3>Plateforme de développement économique</h3>
            <p className="afr-muted">
              AFRICTAL n'est pas uniquement une plateforme d'emploi — c'est une plateforme de
              <strong> développement économique panafricain</strong>. Créer, entreprendre, investir,
              se former, se faire coacher : un seul Passeport, une seule identité professionnelle à vie.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c012-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C012<span className="afr-muted"> / Business + AI</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Business, Entrepreneurship, Finance &amp; AI Ecosystem TERMINÉ · ✅ Passeport automatiquement enrichi · Prêt pour <strong>PROMPT C013</strong>
            </div>
          </div>
        </div>
      </section>

      {/* C011 Learning, Certification & Mentoring */}
      <section id="c011" className="afr-section" data-testid="afr-c011">
        <h2 className="afr-section__title">C011 — Learning, Certification & Mentoring Ecosystem</h2>
        <p className="afr-section__lead">
          Plateforme complète d'apprentissage panafricaine — formations, certifications INTERNAL &
          EXTERNAL, badges numériques, attestations vérifiables publiquement, parcours personnalisés,
          mentorat, coaching, communautés (professionnelles, sectorielles, locales), forums, Q&A, FAQ,
          bibliothèque documentaire. Chaque activité clôturée enrichit automatiquement le Passeport
          (<strong>ADR-010</strong> : skills, credentials, badges, timeline, completeness score).
          Points d'intégration Coach IA prêts — aucun moteur IA / paiement / matching / recrutement implémenté.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c011-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Catalogue formations</div>
            <h3>12 secteurs · niveaux Beginner→Expert</h3>
            <p className="afr-muted">
              IT · Télécoms · BTP · Agriculture · Santé · Éducation · Commerce · Industrie · Artisanat ·
              Administration · Énergies · Autres. FTS + trigram sur titres, tsvector sur descriptions.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Modules & leçons</div>
            <h3>7 formats · VIDEO · ARTICLE · PDF · AUDIO · LIVE · QUIZ · EXERCISE</h3>
            <p className="afr-muted">
              Ordonnancement strict, ressources pédagogiques attachées, durée en minutes,
              file_id vers MinIO via Core Platform.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Inscriptions & progression</div>
            <h3>ACTIVE → COMPLETED · progress_pct</h3>
            <p className="afr-muted">
              enrollments + lesson_completions. À COMPLETED, publie CourseCompletedEvent →
              enrichit le Passeport (skills + timeline + completeness_score).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Quiz & Exercices</div>
            <h3>5 kinds · pass_score · time_limit</h3>
            <p className="afr-muted">
              SINGLE · MULTIPLE · TRUE_FALSE · OPEN · CODE. Exercices avec correction manuelle
              (submissions + grade + feedback markdown).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Certifications</div>
            <h3>INTERNAL & EXTERNAL · vérification publique</h3>
            <p className="afr-muted">
              verification_code 12 chars, signature_hash SHA-256 (JWS-ready), validity_days configurable.
              GET /api/v1/certifications/verify/{"{"}code{"}"} — public, sans auth.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Badges & Attestations</div>
            <h3>Open Badges · evidence_url · PDF attestations</h3>
            <p className="afr-muted">
              badges + badge_awards (evidence_url) · attestations avec verification_code
              vérifiable publiquement (/attestations/verify/{"{"}code{"}"}).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Mentorat</div>
            <h3>Mentors · Sessions · Feedbacks · REQUESTED→COMPLETED</h3>
            <p className="afr-muted">
              Profils mentors (expertise_tags, hourly_rate, languages, disponibilité), demandes,
              sessions planifiées, feedbacks 1-5. Mentorship.completed enrichit le Passeport.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Communautés</div>
            <h3>4 kinds · PROFESSIONAL · SECTORAL · LOCAL · LEARNING_GROUP</h3>
            <p className="afr-muted">
              Adhésions (community_members OWNER/MODERATOR/MEMBER), slug unique, visibilité
              publique/privée, compteur de membres.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Forums & Q&A</div>
            <h3>Threads + posts hiérarchisés · questions/réponses/upvotes</h3>
            <p className="afr-muted">
              forum_threads + forum_posts (parent_post_id pour thread), questions + answers
              (accepted_answer_id). FTS trigram sur titres.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">FAQ & Bibliothèque</div>
            <h3>FAQ éditoriales · Documents publics</h3>
            <p className="afr-muted">
              faqs (curées par plateforme/mentors), library_documents (PDF · VIDEO · ARTICLE · DATASET · LINK).
              Endpoints publics /faq et /library.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Coach IA (points d'intégration)</div>
            <h3>ai_coach_recommendations · 0 algorithme</h3>
            <p className="afr-muted">
              Table prête pour 4 kinds : COURSE · MENTOR · CERTIFICATION · LEARNING_PATH.
              Endpoint /ai-coach/recommendations = 501 tant que le moteur n'est pas activé.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">ADR-010 · Passeport auto-enrichi</div>
            <h3>6 événements pivots · idempotents</h3>
            <p className="afr-muted">
              course.completed · certification.awarded · badge.awarded · mentorship.completed ·
              quiz.completed · community.joined. Ledger ADR-005. Contrat CourseCompletedV1 documenté.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c011-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C011<span className="afr-muted"> / Learning</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Learning, Certification &amp; Mentoring Ecosystem TERMINÉ · ✅ Passeport automatiquement enrichi · Prêt pour <strong>PROMPT C012</strong>
            </div>
          </div>
        </div>
      </section>

      {/* C010 Freelance & Marketplace */}
      <section id="c010" className="afr-section" data-testid="afr-c010">
        <h2 className="afr-section__title">C010 — Freelance & Marketplace Ecosystem</h2>
        <p className="afr-section__lead">
          Marketplace africaine de services professionnels adaptée aux réalités locales — numérique,
          technique et artisanat. 24 catégories seedées, 6 types de clients supportés, cycle complet
          Service → Devis → Mission → Contrat → Jalons → Livrables → Validation → Clôture. À la
          clôture, le Passeport est enrichi automatiquement (expérience FREELANCE, portfolio_item,
          skills, timeline) via <strong>ADR-009</strong>. Aucun paiement / wallet / IA / matching
          implémenté — points d'intégration uniquement.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c010-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Marketplace</div>
            <h3>24 catégories · numérique · technique · artisanat</h3>
            <p className="afr-muted">
              IT · Software · Cybersécurité · IA · Réseaux · BTP · Électricité · Plomberie · Menuiserie ·
              Soudure · Mécanique · Agriculture · Élevage · Santé · Enseignement · Comptabilité · Juridique ·
              Marketing · Communication · Design · Audiovisuel · Artisanat · Transport · Logistique.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Packages 3 tiers</div>
            <h3>Basic · Standard · Premium</h3>
            <p className="afr-muted">
              Chaque service porte 3 formules : prix, devise ISO 4217, délai de livraison, révisions, features
              JSONB. Contraintes de cohérence auto-vérifiées en base.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Mission lifecycle</div>
            <h3>8 statuts · Outbox transactionnel</h3>
            <p className="afr-muted">
              PUBLISHED → ACCEPTED → NEGOTIATION → EXECUTION → DELIVERY → VALIDATION → CLOSED / CANCELLED.
              Historique complet audité avec trace_id OTEL.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Contrats</div>
            <h3>Contenu markdown + SHA-256</h3>
            <p className="afr-muted">
              Chaque contrat est stocké avec son contenu md et un hash SHA-256 au moment de la signature.
              Traçabilité intégrale, aucune re-signature invisible possible.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Jalons & Livrables</div>
            <h3>Milestones · Deliverables MinIO</h3>
            <p className="afr-muted">
              Jalons ordonnés (PENDING → VALIDATED), livrables uploadés dans MinIO
              (via Core Platform files), workflow SUBMITTED → VALIDATED / REJECTED / REVISION.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Avis & Réputation</div>
            <h3>Rating 1-5 · 1 avis par sens</h3>
            <p className="afr-muted">
              Client note freelance, freelance note client. Recomputation asynchrone de la réputation
              agrégée (`reputation_snapshots`) — aucun algorithme dans C010.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Recherche</div>
            <h3>FTS + trigram · filtres · tri</h3>
            <p className="afr-muted">
              GET /api/v1/search/services : q, category, priceMax. Postgres FTS (title trigram +
              description tsvector), tri par pertinence / prix / récent / rating.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Favoris & Recherches</div>
            <h3>Services + recherches sauvegardées</h3>
            <p className="afr-muted">
              favorite_services · saved_searches (JSONB) — persistance par utilisateur, aucune
              synchronisation externe.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Paiements (architecture)</div>
            <h3>Escrow · Mobile Money · Wallet · Factures</h3>
            <p className="afr-muted">
              4 canaux prévus (MOBILE_MONEY, CARD, BANK_TRANSFER, AFRICTAL_WALLET), tables
              `payment_intents`, `escrow_holdings`, `invoices`. <strong>Endpoints répondent
              501 Not Implemented</strong> — sprint dédié.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">ADR-009 · Passeport auto-enrichi</div>
            <h3>Mission CLOSED → new experience FREELANCE</h3>
            <p className="afr-muted">
              Event `africtal.freelance.mission.completed.v1` → passport-service :
              experience + portfolio_item + skills + timeline. Idempotent via ledger ADR-005 (missionId).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Outbox transactionnel</div>
            <h3>13 événements Kafka · DLQ · versioning .v1</h3>
            <p className="afr-muted">
              ServiceCreated/Updated/Published · QuoteRequested · MissionCreated/Accepted/Completed ·
              MilestoneCompleted · DeliverableSubmitted/Validated · ContractSigned · ReviewAdded ·
              PortfolioUpdatedFromMission. Tous <code>.v1</code>, DLQ déclarée.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Frontend Design System C004</div>
            <h3>15 écrans · Zustand · Framer · WCAG AA</h3>
            <p className="afr-muted">
              /marketplace · /marketplace/search · /services/[id] · /services/new · /services/[id]/edit ·
              /freelancers/[id] · /clients/me · /quotes/new · /quotes/[id] · /missions/[id] ·
              /missions/[id]/contract · /missions/[id]/pipeline · /dashboard/freelance ·
              /dashboard/client · /favorites.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c010-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C010<span className="afr-muted"> / Freelance</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Freelance & Marketplace Ecosystem TERMINÉ · ✅ Passeport automatiquement enrichi · Prêt pour <strong>PROMPT C011</strong>
            </div>
          </div>
        </div>
      </section>

      {/* V008-V009 Audit */}
      <section id="v89" className="afr-section" data-testid="afr-v89">
        <h2 className="afr-section__title">Audit V008-V009 · comité Talent & Employment (13 experts)</h2>
        <p className="afr-section__lead">
          Audit global des deux écosystèmes C008 (Talent) et C009 (Employment) et de leurs interactions
          avec le Passeport Numérique et la Core Platform. Score final :
          <strong> {v89?.globalScore ?? 98.5} / 100</strong> · Seuil : 98/100 ·
          Verdict : <strong style={{color:"var(--afr-success)"}}>{v89?.verdict ?? "VALIDATED"}</strong>.
        </p>

        <div className="afr-audit-grid" data-testid="afr-v89-grid">
          {(v89?.scores ?? []).map((row) => (
            <div key={row.dimension} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.dimension}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-v89-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {v89?.globalScore ?? 98.5}<span className="afr-muted"> / 100</span>
            </div>
            <div className="afr-audit-summary__pct">Seuil requis : 98</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">
              ✅ C008 VALIDÉ · ✅ C009 VALIDÉ · Talent &amp; Employment Enterprise Ready
            </div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">Vérifications d'intégration Talent · Employment · Passport</h3>
          <ul data-testid="afr-v89-integration">
            {(v89?.integrationChecks ?? []).map((c) => (
              <li key={c.flow}>
                <strong style={{color:"var(--afr-success)"}}>{c.status}</strong> — {c.flow}
              </li>
            ))}
          </ul>
        </div>

        <div className="afr-corrections" style={{marginTop:"1.5rem"}}>
          <h3 className="afr-corrections__title">12 corrections automatiques appliquées</h3>
          <ul data-testid="afr-v89-corrections">
            {(v89?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>

        <div className="afr-corrections" style={{marginTop:"1.5rem"}}>
          <h3 className="afr-corrections__title">ADR mis à jour ({v89?.adrsImpacted?.length ?? 2})</h3>
          <ul data-testid="afr-v89-adrs">
            {(v89?.adrsImpacted ?? []).map((a) => (
              <li key={a.id}><strong>{a.id}</strong> — {a.title} <span className="afr-muted">({a.status})</span></li>
            ))}
          </ul>
        </div>
      </section>

      {/* C009 Employment Ecosystem */}
      <section id="c009" className="afr-section" data-testid="afr-c009">
        <h2 className="afr-section__title">C009 — Employment Ecosystem</h2>
        <p className="afr-section__lead">
          Écosystème complet de gestion de l'emploi : offres, candidatures, pipeline de recrutement,
          entretiens, recherche, favoris, alertes, historique. Ouvert à <strong>6 types d'employeurs</strong>
          (Entreprise, Particulier, Organisation, Administration, ONG, Université) × <strong>8 contrats</strong>
          (CDI, CDD, Stage, Alternance, Freelance, Mission, Consulting, Temps partiel) × <strong>3 modes</strong>
          (présentiel, télétravail, hybride). Chaque recrutement acté <em>enrichit automatiquement</em> le
          Passeport Numérique via <strong>ADR-008</strong>. Aucune IA, aucun paiement, aucune Marketplace.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c009-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Aggregate JobOffer</div>
            <h3>Publication · Validation · Expiration · Archivage</h3>
            <p className="afr-muted">
              Cycle DRAFT → PENDING_VALIDATION → PUBLISHED → EXPIRED / CLOSED / ARCHIVED.
              Duplication d'offres, statistiques, indexation FTS PostgreSQL (trigram + tsvector).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Aggregate Application</div>
            <h3>Postuler · Retirer · Statuts · Pièces jointes</h3>
            <p className="afr-muted">
              Pipeline SUBMITTED → SHORTLISTED → INTERVIEW → EVALUATED → OFFER → ACCEPTED/REJECTED/WITHDRAWN.
              Historique d'audit trail avec `actor_user_id` + `trace_id` OTEL.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Aggregate Interview</div>
            <h3>Planification · Participants · Compte rendu</h3>
            <p className="afr-muted">
              4 modes : ON_SITE · VIDEO_CALL · PHONE · TAKE_HOME. Rating 1-5, participants multiples
              (interviewer, candidate, observer), meeting URL. Aucune visio embarquée (prépa uniquement).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">6 types d'employeurs</div>
            <h3>Entreprise · Particulier · ONG · Université…</h3>
            <p className="afr-muted">
              COMPANY · INDIVIDUAL · ORGANIZATION · ADMINISTRATION · NGO · UNIVERSITY.
              Équipes RH avec 5 rôles : OWNER · HR_LEAD · RECRUITER · HIRING_MANAGER · VIEWER.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Taxonomie emploi</div>
            <h3>Secteurs · Métiers · Spécialisations · 7 niveaux</h3>
            <p className="afr-muted">
              Référentiels multilingues (FR/EN), 7 niveaux d'expérience seed (INTERN → EXECUTIVE),
              compétences requises reliées au catalogue Passport (skills_catalog).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Recherche</div>
            <h3>Multicritère · FTS · Suggestions</h3>
            <p className="afr-muted">
              GET /api/v1/search/jobs : filtres contrat, mode, pays, niveau, secteur, salaire.
              Tri : relevance | recent | salary_asc/desc. Suggestions typeahead sur titres.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Favoris & Alertes</div>
            <h3>Offres sauvegardées · Alertes fréquentielles</h3>
            <p className="afr-muted">
              favorite_offers · saved_applications · job_alerts (INSTANT/DAILY/WEEKLY).
              Persistance par utilisateur, réactivation, `last_triggered`.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Historique</div>
            <h3>Candidatures · Recrutements · Offres</h3>
            <p className="afr-muted">
              recruitment_history relie candidat/offre/employeur/passeport-experience-id.
              job_offer_history conserve les snapshots JSONB.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Matching (prep)</div>
            <h3>Schéma prêt · 0 algorithme</h3>
            <p className="afr-muted">
              Table match_scores (skills · location · availability) + algorithme placeholder.
              Un futur matching-service consommera les événements et remplira les scores.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">ADR-008 · Passeport auto-enrichi</div>
            <h3>RecruitmentCompleted → new Experience</h3>
            <p className="afr-muted">
              À l'acceptation, event Kafka `africtal.employment.recruitment.completed.v1` déclenche
              la création d'une expérience PROFESSIONAL dans le Passeport (idempotent · ledger ADR-005).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Outbox transactionnel</div>
            <h3>9 événements Kafka · at-least-once</h3>
            <p className="afr-muted">
              JobPublished · JobUpdated · JobClosed · ApplicationSubmitted · Withdrawn · Transitioned ·
              InterviewScheduled · Completed · RecruitmentCompleted. Producer idempotent + acks=all.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Frontend Design System C004</div>
            <h3>11 écrans · Zustand · Framer · WCAG AA</h3>
            <p className="afr-muted">
              /jobs · /jobs/[id] · /jobs/new · /jobs/[id]/edit · /dashboard/recruiter · /dashboard/candidate ·
              /applications · /jobs/[id]/pipeline (kanban) · /search · /favorites · /alerts.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c009-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C009<span className="afr-muted"> / Employment</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Employment Ecosystem TERMINÉ · ✅ Intégration Passeport effectuée · Prêt pour <strong>PROMPT V009</strong>
            </div>
          </div>
        </div>
      </section>

      {/* C008 Talent Ecosystem */}
      <section id="c008" className="afr-section" data-testid="afr-c008">
        <h2 className="afr-section__title">C008 — Talent Ecosystem</h2>
        <p className="afr-section__lead">
          Extension expressive du Passeport pour les Talents : portfolio détaillé, réalisations,
          endorsements, talent cards partageables, réputation. Le Passeport reste la source de vérité —
          Talent enrichit sans dupliquer <strong>(ADR-007)</strong>. Aucun moteur IA, aucun matching.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c008-grid">
          {(c008?.capabilities ?? []).map((cap, idx) => (
            <article key={cap} className="afr-card afr-card--c002">
              <div className="afr-card__badge">Capability {idx + 1}</div>
              <h3>{cap}</h3>
              <p className="afr-muted">
                Livré dans <span className="afr-mono">talent-service</span> · Clean · Hexagonal · DDD ·
                Outbox transactionnel · 12 règles ArchUnit auto-vérifiées.
              </p>
            </article>
          ))}
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Événements Kafka</div>
            <h3>{c008?.events?.publishes?.length ?? 6} events publiés</h3>
            <p className="afr-muted">
              africtal.talent.profile.updated · portfolio.added · realization.added · endorsement.received ·
              card.shared · <strong>enriched-passport</strong>.
            </p>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">ADR-007</div>
            <h3>Talent enrichit le Passeport</h3>
            <p className="afr-muted">
              Frontière claire avec le Passeport (source de vérité). Aucune duplication de skills/experiences.
              Les événements Talent alimentent la vue enrichie du Passeport.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c008-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C008<span className="afr-muted"> / Talent</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Talent Ecosystem opérationnel · Passeport enrichi automatiquement · <strong>PROMPT V008</strong> disponible
            </div>
          </div>
        </div>
      </section>

      {/* V007 Audit */}
      <section id="v007" className="afr-section" data-testid="afr-v007">
        <h2 className="afr-section__title">Audit V007 · comité Passeport Numérique (12 experts)</h2>
        <p className="afr-section__lead">
          Enterprise Software Architect · DDD Expert · Product Owner AFRICTAL · UX Research · UI Design ·
          Database Architect · API Architect · Security Architect · QA · Performance · Accessibility WCAG 2.2 ·
          Career Development. Score final : <strong>{v007?.globalScore ?? 98.9} / 100</strong> · Seuil : 98/100 ·
          Verdict : <strong style={{color:"var(--afr-success)"}}>{v007?.verdict ?? "VALIDATED"}</strong>.
        </p>

        <div className="afr-audit-grid" data-testid="afr-v007-grid">
          {(v007?.scores ?? []).map((row) => (
            <div key={row.dimension} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.dimension}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-v007-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {v007?.globalScore ?? 98.9}<span className="afr-muted"> / 100</span>
            </div>
            <div className="afr-audit-summary__pct">Seuil requis : 98</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">
              ✅ C007 VALIDÉ · Passeport Numérique AFRICTAL Enterprise Ready · Prêt pour PROMPT C008
            </div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">14 corrections automatiques appliquées</h3>
          <ul>
            {(v007?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>

        <div className="afr-corrections" style={{marginTop:"1.5rem"}}>
          <h3 className="afr-corrections__title">ADR impactés ({v007?.adrsImpacted?.length ?? 6})</h3>
          <ul data-testid="afr-v007-adrs">
            {(v007?.adrsImpacted ?? []).map((a) => (
              <li key={a.id}><strong>{a.id}</strong> — {a.title} <span className="afr-muted">({a.status})</span></li>
            ))}
          </ul>
        </div>
      </section>

      {/* C007 Passeport Numérique */}
      <section id="c007" className="afr-section" data-testid="afr-c007">
        <h2 className="afr-section__title">C007 — Passeport Numérique AFRICTAL</h2>
        <p className="afr-section__lead">
          Identité professionnelle numérique évolutive à vie. <strong>Pas un CV.</strong> Un utilisateur ⇒
          un seul passeport. Enrichissement automatique par tous les modules AFRICTAL (Formation, Certification,
          Freelance, Recrutement, Coach IA, Évaluations, Recommandations, Badges). Aucune duplication de données.
          Aucun moteur IA, aucun calcul de Score AFRICTAL (architecture uniquement).
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c007-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Passeport à vie</div>
            <h3>Un seul, évolutif, non-CV</h3>
            <p className="afr-muted">
              Le Passeport accompagne l'utilisateur toute sa vie. Aucune recréation lors des changements
              de rôle. Snapshot des infos affichables pour un partage public autonome (indépendant du profil
              Core Platform).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">5 familles · 5 niveaux</div>
            <h3>Compétences complètes</h3>
            <p className="afr-muted">
              TECHNICAL · SOFT · LANGUAGE · DIGITAL · BUSINESS. Niveaux : BEGINNER · INTERMEDIATE · ADVANCED ·
              EXPERT · MASTER. Catalog global FTS FR/EN + skills custom, endorsements, années de pratique,
              vérification par certif/badge/mission.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">7 types d'expériences</div>
            <h3>Pro · Stage · Alternance · Freelance…</h3>
            <p className="afr-muted">
              PROFESSIONAL · INTERNSHIP · ALTERNANCE · FREELANCE · VOLUNTEER · PERSONAL_PROJECT · ENTREPRENEURSHIP.
              Chaque expérience : poste, organisation, description, résultats, dates, compétences utilisées (N-N),
              contrat, localisation, remote.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">5 formations · 4 certifs</div>
            <h3>Diplômes · Bootcamps · Badges…</h3>
            <p className="afr-muted">
              Educations : DIPLOMA · CERTIFICATE · BOOTCAMP · COURSE · SELF_LEARNING. Certifications :
              PROFESSIONAL · BADGE · ATTESTATION · LICENSE. Preuves URL, credentialId, expiration, vérification externe.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">10 kinds portfolio</div>
            <h3>Projets · GitHub · Behance · Dribbble…</h3>
            <p className="afr-muted">
              REALIZATION · PROJECT · IMAGE · DOCUMENT · VIDEO · LINK · GITHUB · BEHANCE · DRIBBBLE · WEBSITE.
              Réordonnable, featured, thumbnails, tags. Fichiers stockés via Core Platform files (MinIO).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Objectifs & Mobilité</div>
            <h3>Métier · Mobilité · Contrat · Salaire</h3>
            <p className="afr-muted">
              6 mobility scopes (CITY→REMOTE), 7 contract types (CDI, CDD, INTERIM, FREELANCE, INTERNSHIP,
              ALTERNANCE, VOLUNTEER). Salaire optionnel (min/max/currency/period). Countries + cities cibles.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Visibilité fine</div>
            <h3>PUBLIC · MEMBERS · PRIVATE + par section</h3>
            <p className="afr-muted">
              14 flags de visibilité par section (general, presentation, location, contact, languages,
              availability, situation, skills, experiences, educations, certifications, portfolio,
              objectives, salary_expectations). Contrôle total de l'utilisateur.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Partage QR</div>
            <h3>Lien public + QR 512px · révocable</h3>
            <p className="afr-muted">
              POST /shares → token 192 bits + slug + QR PNG (ZXing, error correction H) stocké MinIO
              presigned. GET /public/passport/{"{"}token{"}"} respecte les flags de visibilité. Révocation
              instantanée, historique des vues (viewCount, lastViewedAt).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Enrichissement auto</div>
            <h3>5 consumers Kafka scaffoldés</h3>
            <p className="afr-muted">
              africtal.training.completed · certification.issued · freelance.mission.completed ·
              recruitment.hired · badge.awarded. Handlers désactivés en C007 (aucun module amont livré),
              packages prêts pour V007+ activation.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Score AFRICTAL prêt</div>
            <h3>5 scores en schéma, 0 algorithme</h3>
            <p className="afr-muted">
              Table passport_scores : completeness_score · skills_score · experience_score · trust_score ·
              employability_score. Aucun calcul en C007 — un futur scoring-service consommera les événements
              africtal.passport.* et écrira ici.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Clean · Hexagonal · DDD</div>
            <h3>1 aggregate · 12 VOs · 13 events · 12 ArchUnit rules</h3>
            <p className="afr-muted">
              Aggregate Passport + 6 entities · 12 value objects · 13 domain events · Outbox transactionnel
              (doctrine V002 F04) · 11 controllers · 11 use-cases · 11 ports sortants · 12 règles ArchUnit
              auto-vérifiées.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Frontend</div>
            <h3>10 écrans · Design System C004</h3>
            <p className="afr-muted">
              /passport · /passport/edit · /passport/skills|experiences|educations|certifications|portfolio/new ·
              /passport/share · /p/[token] (public) · /passport/history. Zustand · Framer Motion · Sonner ·
              dark mode · WCAG AA.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c007-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C007<span className="afr-muted"> / Passeport</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Passeport Numérique opérationnel · Prêt pour <strong>PROMPT V007</strong>
            </div>
          </div>
        </div>
      </section>

      {/* V006 Audit */}
      <section id="v006" className="afr-section" data-testid="afr-v006">
        <h2 className="afr-section__title">Audit V006 · comité Core Platform (14 experts)</h2>
        <p className="afr-section__lead">
          Enterprise Software Architect · DDD Expert · Clean Architecture · Hexagonal · Spring Boot / Java 21 ·
          PostgreSQL · Kafka Event-Driven · MinIO / S3 · Security Architect OWASP ASVS L2 · Performance Engineer ·
          Observability OpenTelemetry · UX Research · Product · QA Test Automation.
          Score final : <strong>{v006?.globalScore ?? 98.6} / 100</strong> · Seuil : 98/100 ·
          Verdict : <strong style={{color:"var(--afr-success)"}}>{v006?.verdict ?? "VALIDATED"}</strong>.
        </p>

        <div className="afr-audit-grid" data-testid="afr-v006-grid">
          {(v006?.scores ?? []).map((row) => (
            <div key={row.dimension} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.dimension}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-v006-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {v006?.globalScore ?? 98.6}<span className="afr-muted"> / 100</span>
            </div>
            <div className="afr-audit-summary__pct">Seuil requis : 98</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">
              ✅ C006 VALIDÉ · Core Platform Enterprise Ready · Prêt pour PROMPT C007
            </div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">14 corrections automatiques appliquées (sans logique métier ajoutée)</h3>
          <ul>
            {(v006?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* C006 Core Platform */}
      <section id="c006" className="afr-section" data-testid="afr-c006">
        <h2 className="afr-section__title">C006 — Core Platform</h2>
        <p className="afr-section__lead">
          Socle transverse de la plateforme AFRICTAL. Un compte unique — évolutif dans le temps —
          des organisations, des membres, des préférences, un centre de notifications, une gestion de fichiers
          MinIO. Rien d'autre. Aucune logique métier.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c006-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Profil unique</div>
            <h3>Un seul compte, une vie d'évolutions</h3>
            <p className="afr-muted">
              Étudiant → Freelance → Employeur → Créateur → Mentor → Investisseur → Administrateur.
              Le compte reste identique — les rôles évoluent. Bio, photo, langues parlées, disponibilité, réseaux sociaux.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">10 types d'organisations</div>
            <h3>Entreprise · Université · ONG · Startup…</h3>
            <p className="afr-muted">
              COMPANY · UNIVERSITY · SCHOOL · TRAINING_CENTER · ASSOCIATION · NGO · ADMINISTRATION ·
              INCUBATOR · ACCELERATOR · STARTUP. Chaque organisation a ses membres et 4 rôles :
              OWNER · ADMIN · MEMBER · VIEWER.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">22 endpoints</div>
            <h3>REST · OpenAPI 3.1 · Bearer JWT</h3>
            <p className="afr-muted">
              profile · preferences · notifications (inbox + read/archive + unread-count) ·
              organizations (CRUD + members invite/remove/role) · files (upload multipart + presigned URL) ·
              countries · languages · cities.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">MinIO S3</div>
            <h3>Photos · CV · Portfolios · Documents</h3>
            <p className="afr-muted">
              5 buckets dédiés · presigned URLs 15 min · max 20 MB · auto-création · checksum SHA-256 ·
              attachments polymorphes (fichier ↔ entité).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">11 tables · 1 migration</div>
            <h3>Flyway V001 · Référentiels seed</h3>
            <p className="afr-muted">
              profiles · organizations · organization_members · user_preferences · notification_preferences ·
              notifications · files · attachments · countries (24 seed) · languages (12 seed) · cities. Aucune table métier.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">7 événements Kafka</div>
            <h3>Async — prêt pour tous les modules à venir</h3>
            <p className="afr-muted">
              ProfileUpdated · OrganizationCreated · MemberInvited · MemberRemoved · NotificationRead ·
              PreferencesUpdated · FileUploaded. Topics <code>africtal.core.*</code>.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Clean · Hexagonal · DDD</div>
            <h3>3 aggregates · 8 ports · 4 services</h3>
            <p className="afr-muted">
              Profile · Organization (+ Member entity) · Notification. Ports groupés dans CorePorts.
              Services : ProfileService · OrganizationService · NotificationAndPreferencesService · FileService.
              Adapter MinIO + Kafka.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Frontend</div>
            <h3>8 écrans · Design System C004</h3>
            <p className="afr-muted">
              /profile · /profile/edit · /preferences · /organizations · /organizations/new ·
              /organizations/[id]/members · /settings · /notifications. Zustand · Framer Motion ·
              Sonner · dark mode · WCAG AA.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c006-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C006<span className="afr-muted"> / Core</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Core Platform Enterprise opérationnelle · Prêt pour <strong>PROMPT V006</strong>
            </div>
          </div>
        </div>
      </section>

      {/* V005 Audit */}
      <section id="v005" className="afr-section" data-testid="afr-v005">
        <h2 className="afr-section__title">Audit V005 · comité Identity & Access (12 experts)</h2>
        <p className="afr-section__lead">
          Enterprise Architect · IAM Architect · Keycloak Expert · Spring Security · OAuth2/OIDC ·
          UX Research · Product · Security · QA · DevSecOps · Performance · Accessibility WCAG 2.2.
          Score final : <strong>{v005?.globalScore ?? 98.6} / 100</strong> · Seuil : 98/100 ·
          Verdict : <strong style={{color:"var(--afr-success)"}}>{v005?.verdict ?? "VALIDATED"}</strong>.
        </p>

        <div className="afr-audit-grid" data-testid="afr-v005-grid">
          {(v005?.scores ?? []).map((row) => (
            <div key={row.dimension} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.dimension}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-v005-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {v005?.globalScore ?? 98.6}<span className="afr-muted"> / 100</span>
            </div>
            <div className="afr-audit-summary__pct">Seuil requis : 98</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">
              ✅ C005 VALIDÉ · Identity & Access Platform Enterprise Ready · Prêt pour PROMPT C006
            </div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">14 corrections automatiques appliquées (sans friction UX)</h3>
          <ul>
            {(v005?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* C005 Identity Module */}
      <section id="c005" className="afr-section" data-testid="afr-c005">
        <h2 className="afr-section__title">C005 — Module Identity & Authentication</h2>
        <p className="afr-section__lead">
          Premier module fonctionnel AFRICTAL. Un compte unique, un persona évolutif, une sécurité invisible.
          Aucun autre module métier — passeport, talents, freelance, recrutement, paiement et IA arrivent plus tard.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c005-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Philosophie</div>
            <h3>Inscription &lt; 2 min · Sécurité invisible</h3>
            <p className="afr-muted">
              1 seul compte AFRICTAL · persona évolutif sans recréation · MFA optionnelle (activation future) ·
              expérience utilisateur simple, rapide et rassurante. Rien d'obligatoire hors du strict nécessaire.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">11 Personas</div>
            <h3>Un choix, une évolution possible</h3>
            <p className="afr-muted">
              Talent · Freelance · Employeur · Entreprise · Université · Formateur · Étudiant · Investisseur ·
              Mentor · Coach. Admin réservé au Super Administrateur.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">12 endpoints</div>
            <h3>REST · OpenAPI 3.1 · Bearer JWT</h3>
            <p className="afr-muted">
              register · login · logout · refresh (rotatif) · forgot-password · reset-password · verify-email ·
              resend-verification · users/me · users/profile (GET+PUT) · personas/catalog. RFC 7807 pour toutes les erreurs.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Sécurité multi-couches</div>
            <h3>BCrypt · JWT · Bucket4j · Brute-force lock</h3>
            <p className="afr-muted">
              BCrypt strength 12 · JWT HS256→RS256 · access 15min · refresh 7j (30j avec Remember Me) ·
              rotation + reuse detection · 5 échecs → LOCK 15min · rate-limit register 5/min · login 10/min ·
              forgot 3/min · réponses forgot-password volontairement silencieuses (pas de leak).
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">8 tables · 3 migrations</div>
            <h3>PostgreSQL 16 · Flyway · CITEXT</h3>
            <p className="afr-muted">
              users · roles · user_roles · sessions · refresh_tokens · email_verification_tokens ·
              password_reset_tokens · login_history. Aucune table métier. Extensions pgcrypto & citext.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Keycloak 25</div>
            <h3>Realm africtal · IdP sociaux préparés</h3>
            <p className="afr-muted">
              Realm complet · clients Web (PKCE S256) + backend + mobile · password policy PBKDF2-SHA512 210k ·
              brute-force protection · Google · Microsoft · Apple · Facebook déclarés, désactivés jusqu'à
              obtention des credentials.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Clean · Hexagonal · DDD</div>
            <h3>1 aggregate · 11 ports · 12 règles ArchUnit</h3>
            <p className="afr-muted">
              Aggregate User · 6 value objects (Email, CountryCode, PasswordHash, FullName, Persona, AccountStatus) ·
              3 domain events publiés sur Kafka (topics africtal.identity.*) · 11 ports de sortie ·
              12 règles ArchUnit vérifiées automatiquement.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Frontend</div>
            <h3>8 écrans · Design System C004</h3>
            <p className="afr-muted">
              Welcome · Choose-persona · Register · Verify-email · Login · Forgot-password · Reset-password · Profile.
              Zustand + Zod · Framer Motion · Sonner · dark mode natif · WCAG AA · a11y-first · animations micro-interactions.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c005-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C005<span className="afr-muted"> / IAM</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Module IAM livré · Authentification Enterprise opérationnelle · Prêt pour <strong>PROMPT V005</strong>
            </div>
          </div>
        </div>
      </section>

      {/* Hero-Bottom-Filler was V004 — keep V004 as-is */}

      {/* V004 Audit */}
      <section id="v004" className="afr-section" data-testid="afr-v004">
        <h2 className="afr-section__title">Audit V004 · comité Frontend (14 experts)</h2>
        <p className="afr-section__lead">
          VP Engineering · Principal Frontend Architect · UX/UI Directors · WCAG 2.2 Expert ·
          React Core · Next.js · TypeScript · TailwindCSS · Design System Architect · Product Designer ·
          Mobile UX · Performance Engineer · QA Frontend Lead.
          Score final : <strong>{v004?.globalScore ?? 98.4} / 100</strong> · Seuil : 98/100 ·
          Verdict : <strong style={{color:"var(--afr-success)"}}>{v004?.verdict ?? "VALIDATED"}</strong>.
        </p>

        <div className="afr-audit-grid" data-testid="afr-v004-grid">
          {(v004?.scores ?? []).map((row) => (
            <div key={row.dimension} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.dimension}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-v004-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {v004?.globalScore ?? 98.4}<span className="afr-muted"> / 100</span>
            </div>
            <div className="afr-audit-summary__pct">Seuil requis : 98</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">
              ✅ C004 VALIDÉ · Frontend Enterprise Ready · Prêt pour PROMPT C005
            </div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">14 corrections automatiques appliquées</h3>
          <ul>
            {(v004?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* C004 Frontend */}
      <section id="c004" className="afr-section" data-testid="afr-c004">
        <h2 className="afr-section__title">C004 — Plateforme Frontend Enterprise</h2>
        <p className="afr-section__lead">
          Next.js 15 · React 19 · TypeScript · Tailwind · Radix UI · React Hook Form + Zod ·
          TanStack Query · Zustand · Axios · Framer Motion · next-intl · Storybook 8 · Vitest.
          Design System complet, 29 composants accessibles, 6 layouts, 11 personas préparées.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c004-grid">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Design System</div>
            <h3>Tokens · Dark/Light · WCAG AA</h3>
            <p className="afr-muted">
              4 palettes (canvas/surface/signal + accents kola/amber/emerald/crimson) · 3 familles typo
              (Neue Machina · Manrope · JetBrains Mono) · échelle de 10 tailles · 6 rayons · 5 ombres ·
              6 breakpoints · z-index tokens · durations & easings motion.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">UI Kit (29)</div>
            <h3>Radix + cva + forwardRef</h3>
            <p className="afr-muted">
              Button, Input, Card, Dialog, Tabs, Checkbox, Radio, Switch, Select, Popover, Tooltip,
              Progress, Accordion, Table, Skeleton, Avatar, Badge, EmptyState, ErrorState,
              SuccessState, Breadcrumbs, SearchBar, Pagination, Toaster (sonner)…
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Layouts (6)</div>
            <h3>App Router + RSC</h3>
            <p className="afr-muted">
              LandingLayout · DashboardLayout · AuthLayout · AdminLayout · DocsLayout · ErrorLayout.
              Sidebar + Topbar + MobileNav + Footer + Breadcrumbs. Chaque layout inclut skip-link a11y
              et respecte prefers-reduced-motion.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">11 Personas</div>
            <h3>Dashboards persona-based</h3>
            <p className="afr-muted">
              Talent · Freelance · Employer · Company · University · Trainer · Student · Investor ·
              Admin · Coach · Superadmin. Route <span className="afr-mono">/dashboard/[persona]</span> avec
              guards + navigation catalog.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Auth (UI-only)</div>
            <h3>4 formulaires · Zod · a11y</h3>
            <p className="afr-muted">
              SignIn · SignUp · ForgotPassword · MFAChallenge. RouteGuard + AuthProvider placeholder.
              Handlers NO-OP — wiring Keycloak PKCE arrive en C005.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">i18n & a11y</div>
            <h3>FR · EN · RTL-ready · WCAG AA</h3>
            <p className="afr-muted">
              next-intl avec messages/{"{"}fr,en{"}"}.json · locale par défaut FR · dir="rtl" ready.
              Focus-visible obligatoire · skip-link · prefers-reduced-motion respecté · ARIA-first.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Performance</div>
            <h3>App Router · Middleware · PWA</h3>
            <p className="afr-muted">
              Code-splitting natif · dynamic imports · next/image · manifest.webmanifest · theme-color ·
              middleware (correlation-id, headers sécurité). Axios interceptors : Idempotency-Key,
              RFC 7807.
            </p>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">DX & Docs</div>
            <h3>Storybook 8 · Vitest · docs/</h3>
            <p className="afr-muted">
              Storybook 8 (addon a11y + themes) · Vitest + Testing Library · Playwright pour e2e ·
              <span className="afr-mono"> docs/frontend/DESIGN_SYSTEM.md</span> (14 sections) ·
              apps/web/README.md complet.
            </p>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c004-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">C004<span className="afr-muted"> / frontend</span></div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Plateforme Frontend Enterprise prête — en attente du <strong>PROMPT V004</strong>
            </div>
          </div>
        </div>
      </section>

      {/* V003 Audit */}
      <section id="v003" className="afr-section" data-testid="afr-v003">
        <h2 className="afr-section__title">Audit V003 · comité infrastructure (15 experts)</h2>
        <p className="afr-section__lead">
          Audit complet sur 10 dimensions. Seuil : 95/100. Résultat : <strong>{v003?.globalScore ?? 96.4} / 100</strong> ·
          Verdict : <strong style={{color:"var(--afr-success)"}}>{v003?.verdict ?? "VALIDATED"}</strong>.
        </p>

        <div className="afr-audit-grid" data-testid="afr-v003-grid">
          {(v003?.scores ?? []).map((row) => (
            <div key={row.dimension} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.dimension}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-v003-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {v003?.globalScore ?? 96.4}<span className="afr-muted"> / 100</span>
            </div>
            <div className="afr-audit-summary__pct">Seuil requis : 95</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">
              ✅ C003 VALIDÉ — Infrastructure Enterprise prête pour la Phase C004
            </div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">14 corrections automatiques appliquées</h3>
          <ul>
            {(v003?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* C003 — Enterprise Infrastructure */}
      <section id="c003" className="afr-section" data-testid="afr-c003">
        <h2 className="afr-section__title">C003 — Infrastructure Enterprise</h2>
        <p className="afr-section__lead">
          17 composants production-ready couvrant bases de données, streaming, IAM, observabilité,
          sécurité et plateforme Spring Cloud. Chaque composant est livré avec sa documentation
          d'architecture, ses manifests Kubernetes, son intégration Helm & Terraform, ainsi que
          ses configurations Spring Boot associées.
        </p>

        <div className="afr-grid afr-grid--c002" data-testid="afr-c003-grid">
          {(infra?.components ?? []).map((c) => (
            <article key={c.id} className="afr-card afr-card--c002" data-testid={`c003-${c.id}`}>
              <div className="afr-card__badge">{c.category}</div>
              <h3>{c.name}</h3>
              <p className="afr-muted">
                Status : <strong style={{color:"var(--afr-success)"}}>{c.status}</strong>
                <br />
                <span className="afr-mono">{c.doc}</span>
              </p>
            </article>
          ))}
        </div>

        <div className="afr-grid afr-grid--c002" style={{marginTop:"2rem"}}>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge"><Server size={14}/> Deployments</div>
            <h3>Docker Compose + K8s + Helm + Terraform</h3>
            <ul className="afr-muted" style={{marginTop:"0.5rem", paddingLeft:"1rem"}}>
              <li>docker-compose.yml (local dev, tous services)</li>
              <li>12+ manifests Kubernetes (StatefulSet, Deployment, HPA)</li>
              <li>Helm umbrella chart (12 dépendances)</li>
              <li>Terraform modules multi-cloud (10 modules)</li>
              <li>Kustomize overlays : dev / test / preprod / prod</li>
            </ul>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge"><Network size={14}/> Enterprise Pillars</div>
            <h3>Spring Cloud + Resilience + Observability</h3>
            <ul className="afr-muted" style={{marginTop:"0.5rem", paddingLeft:"1rem"}}>
              <li>Service Discovery : {infra?.enterprisePillars?.serviceDiscovery}</li>
              <li>Config Server : Spring Cloud Config (Git + Vault)</li>
              <li>API Gateway : Spring Cloud Gateway (Netty)</li>
              <li>Resilience4j : CB + Retry + Bulkhead + TL + RL</li>
              <li>Distributed Tracing : OpenTelemetry W3C</li>
              <li>Health : liveness / readiness / startup sur :8081</li>
            </ul>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge"><ShieldCheck size={14}/> Security</div>
            <h3>Vault + External Secrets + mTLS + Kyverno</h3>
            <ul className="afr-muted" style={{marginTop:"0.5rem", paddingLeft:"1rem"}}>
              <li>HashiCorp Vault HA (Raft, auto-unseal)</li>
              <li>External Secrets Operator (ClusterSecretStore)</li>
              <li>NetworkPolicies default-deny + allow rules</li>
              <li>Kyverno policies (signed images, no privileged)</li>
              <li>cert-manager (Let's Encrypt + Vault PKI)</li>
              <li>Falco runtime detection</li>
            </ul>
          </article>

          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge"><Cloud size={14}/> Observability Stack</div>
            <h3>Prometheus + Loki + Tempo + Grafana + OTEL</h3>
            <ul className="afr-muted" style={{marginTop:"0.5rem", paddingLeft:"1rem"}}>
              <li>prometheus.yml + alerting rules (SLO-based)</li>
              <li>Loki (7d hot, MinIO backend)</li>
              <li>Tempo (traces, tail sampling)</li>
              <li>OTEL Collector (batching + tail sampling)</li>
              <li>Grafana provisioning (datasources + dashboards)</li>
              <li>Alertmanager → PagerDuty / Slack</li>
            </ul>
          </article>
        </div>

        <div className="afr-audit-summary" data-testid="afr-c003-summary" style={{marginTop:"2rem"}}>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Phase</span>
            <div className="afr-audit-summary__score">
              C003<span className="afr-muted"> / infrastructure</span>
            </div>
            <div className="afr-audit-summary__pct">Status : COMPLETE</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Prochaine étape</span>
            <div className="afr-audit-summary__verdict">
              ✅ Infrastructure Enterprise terminée — en attente du <strong>PROMPT V003</strong>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section id="architecture" className="afr-section" data-testid="afr-principles">
        <h2 className="afr-section__title">Principes directeurs</h2>
        <p className="afr-section__lead">
          L'architecture cible d'AFRICTAL respecte, sans compromis, les 10 principes
          fondamentaux d'une plateforme Enterprise moderne.
        </p>
        <div className="afr-grid afr-grid--principles">
          {PRINCIPLES.map(({ icon: Icon, label }) => (
            <div key={label} className="afr-card afr-card--principle">
              <Icon size={22} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Audit V002 */}
      <section id="audit" className="afr-section" data-testid="afr-audit">
        <h2 className="afr-section__title">Audit V002 · comité d'architecture</h2>
        <p className="afr-section__lead">
          Six architectes ont audité l'ensemble de la plateforme sur 15 points.
          10 corrections automatiques ont été appliquées. Score final : <strong>{audit?.percentage ?? "99.3"} / 100</strong>
          &nbsp;— seuil requis 95/100 largement dépassé.
        </p>

        <div className="afr-audit-grid" data-testid="afr-audit-grid">
          {(audit?.grid ?? []).map((row) => (
            <div key={row.point} className="afr-audit-row">
              <CheckCircle2 size={16} className="afr-audit-row__icon" />
              <span className="afr-audit-row__point">{row.point}</span>
              <span className="afr-audit-row__score afr-mono">{row.score}/{row.max}</span>
            </div>
          ))}
        </div>

        <div className="afr-audit-summary" data-testid="afr-audit-summary">
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Score global</span>
            <div className="afr-audit-summary__score">
              {audit?.totalScore ?? 149}<span className="afr-muted"> / {audit?.maxScore ?? 150}</span>
            </div>
            <div className="afr-audit-summary__pct">{audit?.percentage ?? 99.3} / 100</div>
          </div>
          <div>
            <span className="afr-eyebrow afr-eyebrow--small">Verdict</span>
            <div className="afr-audit-summary__verdict">{audit?.verdict ?? "VALIDATED — ready for C003"}</div>
          </div>
        </div>

        <div className="afr-corrections">
          <h3 className="afr-corrections__title">10 corrections automatiques appliquées</h3>
          <ul>
            {(audit?.correctionsApplied ?? []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Bounded Contexts */}
      <section id="c002" className="afr-section" data-testid="afr-c002">
        <h2 className="afr-section__title">Architecture Enterprise — livraison C002</h2>
        <p className="afr-section__lead">
          Les fondations C001 sont désormais habillées d'une architecture Enterprise complète :
          DDD tactique, Clean Architecture matérialisée, Event-Driven Kafka Avro, API-First,
          sécurité Zero-Trust. 12 documents publiés dans <span className="afr-mono">docs/architecture/</span>.
        </p>
        <div className="afr-grid afr-grid--c002">
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">DDD tactique</div>
            <h3>9 patterns matérialisés</h3>
            <div className="afr-tags">
              {(architecture?.dddPatterns ?? []).map((p) => (
                <span key={p} className="afr-tag afr-tag--service">{p}</span>
              ))}
            </div>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Context Map</div>
            <h3>7 patterns DDD stratégiques</h3>
            <div className="afr-tags">
              {(architecture?.contextMapPatterns ?? []).map((p) => (
                <span key={p} className="afr-tag afr-tag--service">{p}</span>
              ))}
            </div>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Clean Architecture</div>
            <h3>5 couches par service</h3>
            <div className="afr-tags">
              {(architecture?.cleanArchitectureLayers ?? []).map((p) => (
                <span key={p} className="afr-tag afr-tag--service">{p}</span>
              ))}
            </div>
            <p className="afr-muted afr-mt">
              10 règles ArchUnit vérifiées automatiquement dans chaque microservice.
            </p>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Event-Driven</div>
            <h3>Kafka Avro · Outbox · DLQ</h3>
            <p className="afr-muted">
              Bus : {architecture?.eventDriven?.bus ?? "Kafka"} · Format :{" "}
              {architecture?.eventDriven?.format ?? "Avro + Schema Registry"} ·
              Enveloppe canonique : {architecture?.eventDriven?.envelope ?? "DomainEventEnvelope"}.
              Topics catalogués : <strong>{architecture?.eventDriven?.topicsCatalogued ?? 55}</strong>.
            </p>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">API-First</div>
            <h3>OpenAPI 3.1 · RFC 7807</h3>
            <p className="afr-muted">
              Versioning URL majeur uniquement · pagination + cursor · idempotency-key sur POST sensibles ·
              erreurs Problem+JSON · rate-limit par défaut 120/min.
            </p>
          </article>
          <article className="afr-card afr-card--c002">
            <div className="afr-card__badge">Sécurité Zero-Trust</div>
            <h3>Keycloak · OAuth 2.1 · JWT · RBAC + ABAC</h3>
            <p className="afr-muted">
              JWT RS256 (access 5m · refresh 7j rotatif) · secrets Vault ·
              mTLS mesh Linkerd (V002) · chiffrement TLS 1.3 + AES-256 KMS + PII column-level.
              OWASP API Top 10 mitigé.
            </p>
          </article>
        </div>
      </section>

      {/* Bounded Contexts */}
      <section id="contexts" className="afr-section" data-testid="afr-contexts">
        <h2 className="afr-section__title">25 bounded contexts</h2>
        <p className="afr-section__lead">
          Chaque contexte possède son langage ubiquitaire, ses agrégats, sa base
          de données et publie ses événements sur Kafka.
        </p>
        <div className="afr-grid afr-grid--contexts">
          {contexts.map((c) => (
            <article key={c.name} className="afr-card afr-card--context" data-testid={`ctx-${c.name}`}>
              <div className="afr-card__badge">{c.services.length} services</div>
              <h3>{c.name}</h3>
              <p>{c.description}</p>
              <div className="afr-tags">
                {c.services.map((s) => (
                  <span key={s} className="afr-tag afr-tag--service">{s}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="afr-section" data-testid="afr-services">
        <h2 className="afr-section__title">28 microservices scaffoldés</h2>
        <p className="afr-section__lead">
          Chaque squelette embarque Spring Boot 3, Eureka client, Actuator,
          Micrometer, OpenTelemetry, Dockerfile multi-stage et un README dédié.
        </p>
        <div className="afr-table-wrap">
          <table className="afr-table" data-testid="afr-services-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Domaine</th>
                <th>Port</th>
                <th>Rôle</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.name} data-testid={`svc-${s.name}`}>
                  <td className="afr-mono">{s.name}</td>
                  <td><span className="afr-domain-badge">{s.domain}</span></td>
                  <td className="afr-mono">{s.port}</td>
                  <td className="afr-muted">{s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Stack */}
      <section id="stack" className="afr-section" data-testid="afr-stack">
        <h2 className="afr-section__title">Stack technique</h2>
        <div className="afr-grid afr-grid--stack">
          {overview &&
            Object.entries(overview.stack).map(([layer, techs]) => (
              <div key={layer} className="afr-card afr-card--stack">
                <span className="afr-eyebrow afr-eyebrow--small">{layer}</span>
                <ul>
                  {techs.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      </section>

      {/* Health strip */}
      <section className="afr-section afr-section--tight" data-testid="afr-health">
        <div className="afr-health">
          <TerminalSquare size={20} />
          <span>
            <strong>C012</strong> — Business, Entrepreneurship, Finance &amp; AI · {c012?.subModules?.length ?? 17} sous-modules · {c012?.aiCoaches?.count ?? 10} Coachs IA · {c012?.database?.totalTables ?? 24} tables · {c012?.events?.published?.length ?? 15} events · ADR-011
          </span>
          <span className="afr-health__ok">
            <CheckCircle2 size={18} /> ✅ Business, Entrepreneurship, Finance &amp; AI Ecosystem TERMINÉ · ✅ Passeport automatiquement enrichi · ✅ Prêt pour PROMPT C013
          </span>
        </div>
      </section>

      <footer className="afr-footer" data-testid="afr-footer">
        <span>© AFRICTAL — African Talent Platform.</span>
        <span className="afr-muted">Preview Emergent · l'architecture cible reste indépendante.</span>
      </footer>
    </div>
  );
}

function AdminTechniqueHeader() {
  const { user, logout } = useAuth();
  return (
    <div
      className="afr-admin-banner"
      data-testid="afr-admin-banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        flexWrap: "wrap",
        padding: "0.7rem 2rem",
        background: "linear-gradient(90deg, var(--afr-secondary), color-mix(in oklab, var(--afr-secondary) 80%, var(--afr-primary)))",
        color: "#F7F5F0",
        fontFamily: "'Outfit', sans-serif",
        fontSize: "0.85rem",
        letterSpacing: "0.02em",
        borderBottom: "1px solid rgba(247,245,240,0.15)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
        <Terminal size={16} />
        <strong style={{ letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem" }}>
          Administration Technique
        </strong>
        <span style={{ opacity: 0.7 }}>· Accès restreint aux Administrateurs et Super Administrateurs</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
        {user && (
          <span style={{ opacity: 0.9 }} data-testid="admin-banner-user">
            {user.name} · <strong>{user.role}</strong>
          </span>
        )}
        <Link
          to="/admin/ecosysteme"
          data-testid="admin-banner-ecosysteme"
          style={{
            color: "#0F2A42",
            background: "#F7F5F0",
            textDecoration: "none",
            padding: "0.35rem 0.9rem",
            borderRadius: "999px",
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          🌍 Écosystème AFRICTAL
        </Link>
        <Link
          to="/admin/jobs"
          data-testid="admin-banner-jobs"
          style={{
            color: "#0F2A42",
            background: "#F7F5F0",
            textDecoration: "none",
            padding: "0.35rem 0.9rem",
            borderRadius: "999px",
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          🏷️ Référentiel Métiers
        </Link>
        <Link
          to="/admin/mission-matches"
          data-testid="admin-banner-mission-matches"
          style={{
            color: "#0F2A42",
            background: "#F7F5F0",
            textDecoration: "none",
            padding: "0.35rem 0.9rem",
            borderRadius: "999px",
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          Mises en relation missions
        </Link>
        <Link
          to="/dashboard"
          data-testid="admin-banner-back-dashboard"
          style={{
            color: "#F7F5F0",
            textDecoration: "none",
            padding: "0.35rem 0.8rem",
            borderRadius: "999px",
            border: "1px solid rgba(247,245,240,0.3)",
            fontSize: "0.78rem",
          }}
        >
          ← Mon dashboard
        </Link>
        <Link
          to="/"
          data-testid="admin-banner-back-public"
          style={{
            color: "#F7F5F0",
            textDecoration: "none",
            padding: "0.35rem 0.8rem",
            borderRadius: "999px",
            border: "1px solid rgba(247,245,240,0.3)",
            fontSize: "0.78rem",
          }}
        >
          Site public
        </Link>
        {user && (
          <button
            onClick={logout}
            data-testid="admin-banner-logout"
            style={{
              background: "transparent",
              color: "#F7F5F0",
              border: "1px solid rgba(247,245,240,0.3)",
              padding: "0.35rem 0.8rem",
              borderRadius: "999px",
              fontSize: "0.78rem",
              cursor: "pointer",
            }}
          >
            Déconnexion
          </button>
        )}
      </div>
    </div>
  );
}

function PageLoader() {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }} role="status" aria-live="polite" data-testid="page-loader">
      <div className="afr-spinner" aria-hidden="true" />
      <span className="afr-muted" style={{ marginLeft: "0.6rem" }}>Chargement…</span>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="bottom-right"
          richColors
          closeButton
          toastOptions={{
            style: {
              fontFamily: "'Outfit', sans-serif",
              borderRadius: "14px",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<PublicLanding />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/ecosystemes" element={<Ecosystems />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register/student" element={<Navigate to={"/login?mode=signup&role=talent"} replace />} />
          <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><PasswordReset /></Suspense>} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}><Dashboard /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/profile" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Profile /></Suspense></ProtectedRoute>} />
          <Route path="/portfolio" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Portfolio /></Suspense></ProtectedRoute>} />
          <Route path="/company" element={<ProtectedRoute requireRoles={["employeur","admin","super-admin"]}><Suspense fallback={<PageLoader />}><Company /></Suspense></ProtectedRoute>} />
          <Route path="/offers" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Offers /></Suspense></ProtectedRoute>} />
          <Route path="/offers/new" element={<ProtectedRoute requireRoles={["employeur","admin","super-admin","talent","freelance"]}><Suspense fallback={<PageLoader />}><OfferForm /></Suspense></ProtectedRoute>} />
          <Route path="/offers/:id" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><OfferDetail /></Suspense></ProtectedRoute>} />
          <Route path="/offers/:id/edit" element={<ProtectedRoute requireRoles={["employeur","admin","super-admin"]}><Suspense fallback={<PageLoader />}><OfferForm /></Suspense></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Applications /></Suspense></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Messages /></Suspense></ProtectedRoute>} />
          <Route path="/messages/:threadId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Messages /></Suspense></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Notifications /></Suspense></ProtectedRoute>} />
          <Route path="/coach" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Coach /></Suspense></ProtectedRoute>} />
          <Route path="/coach/:sessionId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Coach /></Suspense></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WalletPage /></Suspense></ProtectedRoute>} />
          <Route path="/missions" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Missions /></Suspense></ProtectedRoute>} />
          <Route path="/missions/:mid/chat" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><MissionChat /></Suspense></ProtectedRoute>} />
          <Route path="/admin/mission-matches" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminMissionMatches /></Suspense></ProtectedRoute>} />
          <Route path="/formations" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Formations /></Suspense></ProtectedRoute>} />
          <Route path="/certifications" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Certifications /></Suspense></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Projects /></Suspense></ProtectedRoute>} />
          <Route path="/projects/:id/manage" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ProjectManager /></Suspense></ProtectedRoute>} />
          <Route path="/invitations" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><MyInvitations /></Suspense></ProtectedRoute>} />
          <Route path="/talents" element={<ProtectedRoute requireRoles={["employeur","entrepreneur","admin","super-admin","investisseur"]}><Suspense fallback={<PageLoader />}><Talents /></Suspense></ProtectedRoute>} />
          <Route path="/matching" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Matching /></Suspense></ProtectedRoute>} />
          <Route path="/subscriptions" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Subscriptions /></Suspense></ProtectedRoute>} />
          <Route path="/orchestrator" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Orchestrator /></Suspense></ProtectedRoute>} />
          <Route path="/orchestrator/:projectId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Orchestrator /></Suspense></ProtectedRoute>} />
          <Route path="/workspaces" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Workspaces /></Suspense></ProtectedRoute>} />
          <Route path="/workspaces/:projectId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Workspaces /></Suspense></ProtectedRoute>} />
          <Route path="/admin/command" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminCommand /></Suspense></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminUsers /></Suspense></ProtectedRoute>} />
          <Route path="/admin/offers" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminOffers /></Suspense></ProtectedRoute>} />
          <Route path="/admin/orchestrator" element={<ProtectedRoute requireAdmin><AdminOrchestrator /></ProtectedRoute>} />
          <Route path="/admin/subscriptions" element={<ProtectedRoute requireAdmin><AdminSubscriptions /></ProtectedRoute>} />
          <Route path="/admin/accompagnements" element={<ProtectedRoute requireAdmin><AdminAccompagnements /></ProtectedRoute>} />
          <Route path="/admin/moderation" element={<ProtectedRoute requireAdmin><AdminModeration /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute requireAdmin><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/announcements" element={<ProtectedRoute requireAdmin><AdminAnnouncements /></ProtectedRoute>} />
          <Route path="/admin/broadcast" element={<ProtectedRoute requireAdmin><AdminBroadcast /></ProtectedRoute>} />
          <Route path="/admin/elite" element={<ProtectedRoute requireAdmin><AdminEliteTalents /></ProtectedRoute>} />
          <Route path="/elite" element={<EliteShowcase />} />
          <Route path="/elite/:userId" element={<EliteShowcaseProfile />} />
          {/* V3.23 — Vitrine publique des Talents (profils rendus publics par l'admin) */}
          <Route path="/talents-publics" element={<Suspense fallback={<PageLoader />}><PublicTalents /></Suspense>} />
          <Route path="/talents-publics/:userId" element={<Suspense fallback={<PageLoader />}><PublicTalents /></Suspense>} />
          <Route path="/admin/site-settings" element={<ProtectedRoute requireAdmin><AdminSiteSettings /></ProtectedRoute>} />
          <Route path="/admin/business-model" element={<ProtectedRoute requireAdmin><AdminBusinessModel /></ProtectedRoute>} />
          <Route path="/admin/finance" element={<ProtectedRoute requireAdmin><AdminFinance /></ProtectedRoute>} />
          <Route path="/admin/payment-core" element={<ProtectedRoute requireAdmin><AdminPaymentCore /></ProtectedRoute>} />
          <Route path="/mon-wallet" element={<ProtectedRoute><MyWallet /></ProtectedRoute>} />
          <Route path="/mon-kyc" element={<ProtectedRoute><MyKyc /></ProtectedRoute>} />
          <Route path="/admin/kyc" element={<ProtectedRoute requireAdmin><AdminKyc /></ProtectedRoute>} />
          <Route path="/admin/reactivation" element={<ProtectedRoute requireAdmin><AdminReactivation /></ProtectedRoute>} />
          <Route path="/admin/ecosysteme" element={<ProtectedRoute requireAdmin><AdminEcosystem /></ProtectedRoute>} />
          <Route path="/admin/jobs" element={<ProtectedRoute requireAdmin><AdminJobs /></ProtectedRoute>} />
          <Route path="/admin/freelance-profiles" element={<ProtectedRoute requireAdmin><AdminFreelanceProfiles /></ProtectedRoute>} />
          <Route path="/escrow" element={<ProtectedRoute><MyEscrows /></ProtectedRoute>} />
          <Route path="/escrow/:id" element={<ProtectedRoute><EscrowDetail /></ProtectedRoute>} />
          <Route path="/escrow/:id/deposit" element={<ProtectedRoute><EscrowDeposit /></ProtectedRoute>} />
          <Route path="/admin/escrow" element={<ProtectedRoute requireAdmin><AdminEscrow /></ProtectedRoute>} />
          <Route path="/lab" element={<ProtectedRoute><Lab /></ProtectedRoute>} />
          <Route path="/lab/manage" element={<ProtectedRoute><LabManage /></ProtectedRoute>} />
          <Route path="/admin/competences" element={<ProtectedRoute requireAdmin><AdminCompetences /></ProtectedRoute>} />
          <Route path="/freelance/services" element={<ProtectedRoute requireRoles={["freelance", "admin", "super-admin"]}><FreelanceServices /></ProtectedRoute>} />
          <Route path="/mon-accompagnement" element={<ProtectedRoute><MyAccompaniment /></ProtectedRoute>} />
          <Route path="/tarifs" element={<Pricing />} />
          <Route path="/pour-les-talents" element={<PourLesTalents />} />
          <Route path="/pour-les-employeurs" element={<PourLesEmployeurs />} />
          <Route path="/pour-les-investisseurs" element={<PourLesInvestisseurs />} />
          <Route path="/pour-les-universites" element={<PourLesUniversites />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminTechniqueView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/developer-console"
            element={
              <ProtectedRoute requireAdmin>
                <AdminTechniqueView />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <WelcomeAssistant />
        <PWAInstallBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
